
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { CameraIcon, CloudUploadIcon, CollectionIcon, CubeIcon, CubeTransparentIcon, FilmIcon, GlobeAltIcon, LightBulbIcon, PhotographIcon, SparklesIcon, StarIcon, UserIcon, } from '@heroicons/react/outline';
import { MicrophoneIcon } from '@heroicons/react/outline';
import { QuestionMarkCircleIcon, XIcon } from '@heroicons/react/solid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactTextareaAutosize from 'react-textarea-autosize';
import { useLocalStorage } from 'react-use';
import { toast } from 'sonner';
import { Image as ImageType, ImageModelType, SubscriptionName, TrainingModelType, } from 'types';
import clsxm from '@/lib/clsxm';
import { FREE_MEDIA_QUEUE_ENABLED } from '@/lib/freeMediaQueue';
import { getSubscriptionForEdge } from '@/data/edge';
import { getTrainingModelByTrainedModelIdAndUserId, getTrainingModelsByUserIdAndStatus, } from '@/data/training_models';
import { getProfile } from '@/data/users';
import Modal from '@/components/atoms/Modal';
import Select from '@/components/atoms/Select';
import Button from '@/components/buttons/Button';
import CreditsDisplay from '@/components/credits/CreditsDisplay';
import ActionSelection from '@/components/images/ActionSelection';
import AspectRatioSelector from '@/components/images/AspectRatioSelector';
import EditModelSelector from '@/components/images/EditModelSelector';
import ImageGenerationPreview from '@/components/images/ImageGenerationPreview';
import ImageOutput from '@/components/images/ImageOutput';
import ModelSelectorButton from '@/components/images/ModelSelectorButton';
import NumberInput from '@/components/images/NumberInput';
import OptionCard from '@/components/images/OptionCard';
import ResolutionSelector from '@/components/images/ResolutionSelector';
import SceneModal from '@/components/images/SceneModal';
import UploadedImageCard from '@/components/images/UploadedImageCard';
import { ViewModeSelector } from '@/components/images/ViewModeSelector';
import NarrowDashboardLayout from '@/components/layout/NarrowDashboardLayout';
import ToolsNav from '@/components/layout/ToolsNav';
import ButtonLink from '@/components/links/ButtonLink';
import Seo from '@/components/Seo';
import Spinner from '@/components/Spinner';
import { SimpleTooltip } from '@/components/ui/tooltip';
import VideoOutput from '@/components/videos/VideoOutput';
import { buildCinematicPrompt, CAMERA_OPTIONS, CinematicState, DEFAULT_CINEMATIC_STATE, findMatchingPreset, LIGHTING_OPTIONS, SHOT_TYPE_OPTIONS, } from '@/constants/cinematicShot';
import { getImagesInPlan } from '@/constants/common';
import { DEFAULT_GPT_IMAGE_MODEL, LEGACY_DEFAULT_GPT_IMAGE_MODEL, getGptImage2FamilyCredits, } from '@/constants/gptImageModels';
import { actionsWithDescription } from '@/constants/imageActions';
import { editModels, getDimensionsForModel, isPremiumEditModel, isPremiumImageModel, models, modelsWithDescription, WAVESPEED_T2I_MODELS, } from '@/constants/imageModels';
import { PANORAMA_STYLE_VALUE, STYLE_OPTIONS } from '@/constants/imageStyles';
import { findMediaTemplateForUrl, getMediaTemplateLandingPath, getMediaTemplateLinkParams, getMediaTemplatesByKind, } from '@/constants/mediaTemplates';
import { getNanoBananaCredits, NANO_BANANA_2_CREDITS, NANO_BANANA_2_LITE_CREDITS, NANO_BANANA_PRO_CREDITS, } from '@/constants/nanoBananaCredits';
import { useAnalytics } from '@/contexts/Analytics';
import { compressImage } from '@/utils/compressImage';
import axiosInstance from '@/utils/httpClient';
import { createClient as createComponentClient } from '@/utils/supabase/component';
import { createClient } from '@/utils/supabase/server-props';
import { toastError } from '@/utils/toastError';
import { uploadUserImage } from '@/utils/uploadUserImage';
import { parsePhotoPackSlugs } from '@/utils/photoPackFlow';
import { useUser } from '@/utils/useUser';
const Toggle = dynamic(() => import('@/components/atoms/Toggle'), {
    ssr: false,
});
const AudioRecorder = dynamic(() => import('@/components/Chat/AudioRecorder'), {
    ssr: false,
});
const PricingModal = dynamic(() => import('@/components/PricingModal'), {
    ssr: false,
});
const EditModal = dynamic(() => import('@/components/images/EditModal'), {
    ssr: false,
});
const AuthModal = dynamic(() => import('@/components/AuthModal'), {
    ssr: false,
});
const ExamplesTab = dynamic(() => import('@/components/images/ExamplesTab'));
const ImageToolsGrid = dynamic(() => import('@/components/images/ImageToolsGridIsideComponent'));
const TemplatesGallery = dynamic(() => import('@/components/media/TemplatesGallery'));
const PhotoPacksSelector = dynamic(() => import('@/components/images/PhotoPacksSelector'));
const EMPTY_STATE_SUGGESTIONS = [
    'A cozy coffee shop on a rainy day',
    'Watercolor cityscape at sunset',
    'Playful puppies in a garden',
    'Futuristic spaceship interior',
    'Minimalist logo design',
    'Fantasy castle in the clouds',
];
const RECRAFT_STYLES = [
    'any',
    'realistic_image',
    'digital_illustration',
    'digital_illustration/pixel_art',
    'digital_illustration/hand_drawn',
    'digital_illustration/grain',
    'digital_illustration/infantile_sketch',
    'digital_illustration/2d_art_poster',
    'digital_illustration/handmade_3d',
    'digital_illustration/hand_drawn_outline',
    'digital_illustration/engraving_color',
    'digital_illustration/2d_art_poster_2',
    'realistic_image/b_and_w',
    'realistic_image/hard_flash',
    'realistic_image/hdr',
    'realistic_image/natural_light',
    'realistic_image/studio_portrait',
    'realistic_image/enterprise',
    'realistic_image/motion_blur',
];
// Add vector styles constant
const RECRAFT_VECTOR_STYLES = [
    'vector_illustration',
    'vector_illustration/engraving',
    'vector_illustration/line_art',
    'vector_illustration/line_circuit',
    'vector_illustration/linocut',
];
// Edit models constant (matching what's in PromptForm.tsx)
interface ImagesPageProps {
    imagesInPlan: number;
    totalImages: number;
    images: ImageType[];
    trainingModel: TrainingModelType;
    trainingModels: TrainingModelType[];
    country: string | null;
}
const fetchImages = async (includeProfiles = false) => {
    const url = includeProfiles
        ? '/api/get-images?limit=12&include_profiles=true'
        : '/api/get-images?limit=12';
    const res = await fetch(url);
    if (!res.ok) {
        throw new Error('Network response was not ok');
    }
    return res.json() as unknown as ImageType[];
};
// Rendering engines for the AI Photo Studio flow. Credits must stay in sync
// with the engine routing in /api/generate-image (photoStudioModel branches).
const PHOTO_STUDIO_ENGINES: {
    value: string;
    label: string;
    credits: number;
}[] = [
    { value: 'Hyperrealism', label: 'Hyperrealism', credits: 3 },
    {
        value: 'Nano Banana 2',
        label: 'Nano Banana 2',
        credits: NANO_BANANA_2_CREDITS['1K'],
    },
    {
        value: 'Nano Banana Pro',
        label: 'Nano Banana Pro',
        credits: NANO_BANANA_PRO_CREDITS['1K'],
    },
    {
        value: 'Nano Banana 2 Lite',
        label: 'Nano Banana 2 Lite',
        credits: NANO_BANANA_2_LITE_CREDITS,
    },
    { value: 'Seedream 5 Pro', label: 'Seedream 5 Pro', credits: 8 },
    { value: 'Seedream 5 Lite', label: 'Seedream 5 Lite', credits: 2 },
    { value: 'Legacy', label: 'Legacy', credits: 2 },
];
const getActionTitleFromId = (actionId: string | undefined) => {
    if (!actionId)
        return 'Generate Images';
    const foundAction = actionsWithDescription.find((action) => action.id === actionId);
    return foundAction ? foundAction.title : 'Generate Images';
};
const ImagesPage = ({ imagesInPlan, totalImages, images = [], trainingModel, trainingModels, country, }: ImagesPageProps) => {
    const router = useRouter();
    const supabaseClient = createComponentClient();
    const { subscription, isFreePlan, user, isTeamsPlan } = useUser();
    const [numberOfOutputs, setNumberOfOutputs] = useState(1);
    const [prompt, setPrompt] = useState('');
    const [style, setStyle] = useState('None');
    const [dimensions, setDimensions] = useState(trainingModel?.name ? '1:1' : '1024x1024');
    const [resolution, setResolution] = useState<string | null>(null);
    const useHD = false;
    const [shouldUpgrade, setShouldUpgrade] = useState(false);
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [newBeforeImages, setNewBeforeImages] = useState<string[]>([]);
    const [newObjectImages, setNewObjectImages] = useState<string[]>([]);
    const [newUploadImages, setNewUploadImages] = useState<string[]>([]);
    const [action, setAction] = useState('Edit Image with AI');
    const strength = 8;
    const [searchPrompt, setSearchPrompt] = useState('');
    // Modify this line to use boolean instead of function
    const [promptUpsampling, setPromptUpsampling] = useState(false);
    const [recraftStyle, setRecraftStyle] = useState('any');
    const [useVector, setUseVector] = useState(false);
    const [showAuth, setShowAuth] = useState(false);
    const [authRedirectTo, setAuthRedirectTo] = useState('/ai-images');
    const [showPhotoPacks, setShowPhotoPacks] = useState(false);
    const [photoPackDraft, setPhotoPackDraft] = useState<string[]>([]);
    const [photoPackModelId, setPhotoPackModelId] = useState<number | null>(null);
    useEffect(() => {
        if (router.isReady && router.query.photoPacks === '1') {
            setPhotoPackDraft(parsePhotoPackSlugs(router.query.packs));
            const modelId = Number(router.query.packModel);
            setPhotoPackModelId(Number.isSafeInteger(modelId) && modelId > 0 ? modelId : null);
            setShowPhotoPacks(true);
        }
    }, [
        router.isReady,
        router.query.photoPacks,
        router.query.packs,
        router.query.packModel,
    ]);
    const closePhotoPacks = () => {
        setShowPhotoPacks(false);
        setPhotoPackDraft([]);
        setPhotoPackModelId(null);
        if (router.query.photoPacks) {
            const query = { ...router.query };
            delete query.photoPacks;
            delete query.packs;
            delete query.packModel;
            void router.replace({ pathname: router.pathname, query }, undefined, {
                shallow: true,
                scroll: false,
            });
        }
    };
    const [isPublic, setIsPublic] = useState(true);
    const [useRawMode, setUseRawMode] = useState(false);
    const [hasUsedTwoEmails, setHasUsedTwoEmails] = useState(false);
    const [useLegacyEditModel, setUseLegacyEditModel] = useState(false);
    const [editModel, setEditModel] = useState<string>(DEFAULT_GPT_IMAGE_MODEL);
    // Underlying generation engine for the AI Photo Studio flow
    const [photoStudioModel, setPhotoStudioModel] = useState('Hyperrealism');
    const [moderationStatus, setModerationStatus] = useState<{
        [key: string]: {
            flagged: boolean;
            loading: boolean;
        };
    }>({});
    const [showMicrophoneModal, setShowMicrophoneModal] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    // View mode state with local storage persistence
    const [viewMode, setViewMode] = useLocalStorage<{
        layout: 'grid' | 'row';
        size: 'small' | 'medium' | 'large';
        fill: 'aspect-ratio' | 'square';
    }>('ai-images-view-mode', {
        layout: 'grid',
        size: 'medium',
        fill: 'square',
    });
    const [activeTab, setActiveTab] = useState<'images' | 'tools' | 'examples' | 'templates'>('templates');
    // Check if URL has action parameter
    const hasActionParam = Boolean(router.query.action);
    const [generatedImages, setGeneratedImages] = useState<Partial<ImageType>[]>(images);
    const [pendingGeneration, setPendingGeneration] = useState<{
        count: number;
        startedAt: number;
    } | null>(null);
    const pendingImageCount = pendingGeneration?.count ?? 0;
    const newImageStartTimes = useRef(new Map<string, number>());
    const { t } = useLocale('ai-images');
    const { t: tMedia } = useLocale('media-templates');
    const [localTotalImages, setLocalTotalImages] = useState(totalImages);
    const [bonusImages, setBonusImages] = useState(0);
    // Track image generations for subscription prompt (persists in localStorage)
    // First generation shows normal loading, subsequent ones show queue for free users
    const [generationCount, setGenerationCount] = useLocalStorage<number>('ai-images-generation-count', 0);
    // Track auto-detected dimensions from uploaded images
    const autoDetectedDimensionsRef = useRef<string | null>(null);
    // Track original image dimensions for re-calculation when model changes
    const originalImageDimensionsRef = useRef<{
        width: number;
        height: number;
    } | null>(null);
    const [selectedModels, setSelectedModels] = useState<ImageModelType[]>(() => {
        if (trainingModel?.name) {
            return [trainingModel.name as ImageModelType];
        }
        // Restore the last selection (catalogue models only). The `?model=`
        // query effect and the Free-plan premium sweep both run after mount
        // and override/correct this when needed.
        if (typeof window !== 'undefined') {
            try {
                const stored = JSON.parse(localStorage.getItem('ai-images-selected-models') || 'null');
                if (Array.isArray(stored)) {
                    const valid = stored.filter((m): m is ImageModelType => models.includes(m));
                    // The persist effect below writes the default on every load, so a
                    // lone old default in storage means "never chose" — move it
                    // forward to the current default.
                    if (valid.length === 1 &&
                        valid[0] === LEGACY_DEFAULT_GPT_IMAGE_MODEL) {
                        return [DEFAULT_GPT_IMAGE_MODEL as ImageModelType];
                    }
                    if (valid.length > 0) {
                        return valid;
                    }
                }
            }
            catch {
                // Ignore malformed storage and fall through to the default
            }
        }
        return [DEFAULT_GPT_IMAGE_MODEL as ImageModelType];
    });
    // Remember the model selection across visits. Custom trained models are
    // per-URL, so only catalogue models are persisted; a trained-model-only
    // selection keeps the previous stored value.
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const catalogueModels = selectedModels.filter((m) => models.includes(m));
        if (catalogueModels.length > 0) {
            localStorage.setItem('ai-images-selected-models', JSON.stringify(catalogueModels));
        }
    }, [selectedModels]);
    const [selectedTrainingModel, setSelectedTrainingModel] = useState<TrainingModelType | null>(trainingModel);
    // Backward compatibility: support single model as well
    const model = selectedModels[0];
    const [sceneModalTab, setSceneModalTab] = useState<'character' | 'object' | 'style' | 'camera' | 'lighting' | 'shot-type' | null>(null);
    const [cinematicState, setCinematicState] = useLocalStorage<CinematicState>('ai-images-cinematic-state', DEFAULT_CINEMATIC_STATE);
    const uploadFileInputRef = useRef<HTMLInputElement>(null);
    const analytics = useAnalytics();
    const generatedCaptions = useRef<HTMLHRElement>(null);
    const hasInitializedTab = useRef(false);
    const imageTemplates = getMediaTemplatesByKind('image');
    const [hasProcessingImages, setHasProcessingImages] = useState(false);
    // Grace period after mutation to avoid disabling polling before server marks images as processing
    const pollingGraceUntilRef = useRef<number>(0);
    const client = useQueryClient();
    const { isLoading } = useQuery(['image-generator-images'], () => {
        return fetchImages(isTeamsPlan);
    }, {
        onSuccess(data) {
            // Add queue timestamps for free users:
            // - Images: show queue on 2nd+ generation
            // - Videos: show queue on all generations
            const QUEUE_DURATION_MS = 45 * 60 * 1000; // 45 minutes
            const shouldAddImageQueue = FREE_MEDIA_QUEUE_ENABLED && isFreePlan && (generationCount ?? 0) >= 2;
            const processedData = isFreePlan
                ? data.map((item: ImageType) => {
                    if (!item.created_at)
                        return item;
                    const createdTime = new Date(item.created_at).getTime();
                    const queuedUntil = createdTime + QUEUE_DURATION_MS;
                    // Only add queue if still within the 45 min window
                    if (Date.now() >= queuedUntil)
                        return item;
                    // Videos: always add queue for free users
                    // Images: only add queue on 2nd+ generation
                    if ((item.is_video && FREE_MEDIA_QUEUE_ENABLED) ||
                        shouldAddImageQueue) {
                        return { ...item, _queuedUntil: queuedUntil };
                    }
                    return item;
                })
                : data;
            setGeneratedImages([...processedData]);
            // Videos report processing via the is_processing column; images use
            // an empty image_url as their pending marker (webhook/background job
            // fills it in later), so treat both as "still processing" — otherwise
            // polling stops after the initial grace period and slower models
            // (GPT Image 2, Nano Banana Pro, Seedream fallback) never appear
            // without a manual refresh.
            const stillProcessing = processedData.some((item: ImageType) => item.is_processing ||
                (!item.is_video &&
                    !item.image_url &&
                    !(item.request as any)?.failed));
            if (stillProcessing || Date.now() < pollingGraceUntilRef.current) {
                setHasProcessingImages(true);
            }
            else {
                setHasProcessingImages(false);
            }
            // Switch to images tab on first load if user has images
            if (!hasInitializedTab.current) {
                hasInitializedTab.current = true;
                if (processedData.length > 0) {
                    setActiveTab('images');
                }
            }
            // call api endpoint /api/get-images-credits
            fetch('/api/get-images-credits')
                .then((res) => (res.ok ? res.json() : null))
                .then((data) => {
                if (!data)
                    return;
                setLocalTotalImages(data.totalImages);
                setBonusImages(data.bonus_images);
            })
                .catch(() => { });
        },
        refetchInterval: hasProcessingImages ? 5000 : false,
        enabled: !!user,
    });
    // Downvoting a generation can refund its credits — refresh the usage counter
    // immediately when that happens (the images query may not be polling).
    useEffect(() => {
        const refreshCredits = () => {
            fetch('/api/get-images-credits')
                .then((res) => (res.ok ? res.json() : null))
                .then((data) => {
                if (!data)
                    return;
                setLocalTotalImages(data.totalImages);
                setBonusImages(data.bonus_images);
            })
                .catch(() => { });
        };
        window.addEventListener('credits-refreshed', refreshCredits);
        return () => window.removeEventListener('credits-refreshed', refreshCredits);
    }, []);
    const isCustomModelSelected = trainingModels.some((trainingModel) => trainingModel.name === model);
    const isPhotoStudioModelSelected = Boolean(selectedTrainingModel?.use_photo_studio_flow &&
        selectedTrainingModel?.trained_model_id?.startsWith('photo-studio-'));
    const selectedModelDefinition = useMemo(() => modelsWithDescription.find((item) => item.title === model), [model]);
    const selectedEditModelDefinition = useMemo(() => editModels.find((item) => item.value === editModel), [editModel]);
    const totalUploadedImages = newBeforeImages.length + newObjectImages.length + newUploadImages.length;
    const availableResolutions = useMemo(() => {
        // If in edit mode (images uploaded), use edit model resolutions
        if (action === 'Edit Image with AI' && totalUploadedImages > 0) {
            return selectedEditModelDefinition?.resolutions || [];
        }
        // Otherwise use the regular model resolutions
        return selectedModelDefinition?.resolutions || [];
    }, [
        selectedModelDefinition,
        selectedEditModelDefinition,
        action,
        totalUploadedImages,
    ]);
    // Handle model query parameter for default model selection
    useEffect(() => {
        const modelParam = router.query.model;
        if (modelParam && typeof modelParam === 'string') {
            // First check if it's a training model
            const trainingModel = trainingModels.find((tm) => tm.name === modelParam);
            if (trainingModel) {
                // Use the training model name
                setSelectedModels([modelParam as ImageModelType]);
                setSelectedTrainingModel(trainingModel);
                return;
            }
            // Check if it's a standard model (by title or ID)
            const standardModel = modelsWithDescription.find((m) => m.title === modelParam || m.id === modelParam);
            // Premium models are locked on the Free plan
            if (standardModel &&
                !(isFreePlan && isPremiumImageModel(standardModel.title))) {
                // Always use the model title, not the ID
                setSelectedModels([standardModel.title as ImageModelType]);
                setSelectedTrainingModel(null);
            }
        }
    }, [router.query.model, trainingModels, isFreePlan]);
    // Subscription data can resolve after models were selected (URL param /
    // restored state) — drop premium models from a Free user's selection.
    useEffect(() => {
        if (!isFreePlan)
            return;
        if (selectedModels.some((m) => isPremiumImageModel(m))) {
            const allowed = selectedModels.filter((m) => !isPremiumImageModel(m));
            setSelectedModels(allowed.length > 0
                ? allowed
                : [DEFAULT_GPT_IMAGE_MODEL as ImageModelType]);
        }
    }, [isFreePlan, selectedModels]);
    // Handle editModel query parameter so templates can preset the edit model
    useEffect(() => {
        const editModelParam = router.query.editModel;
        if (editModelParam && typeof editModelParam === 'string') {
            const isValid = editModels.some((m) => m.value === editModelParam);
            // Premium edit models are locked on the Free plan
            if (isValid && !(isFreePlan && isPremiumEditModel(editModelParam))) {
                setEditModel(editModelParam);
            }
        }
    }, [router.query.editModel, isFreePlan]);
    // Subscription data can resolve after the editModel was set (URL param /
    // restored state) — kick Free users off premium edit models when it does.
    useEffect(() => {
        if (isFreePlan && isPremiumEditModel(editModel)) {
            setEditModel(DEFAULT_GPT_IMAGE_MODEL);
        }
    }, [isFreePlan, editModel]);
    useEffect(() => {
        if (isFreePlan) {
            if (!hasUsedTwoEmails) {
                setShouldUpgrade(false);
            }
        }
        setNumberOfOutputs(1);
        if (selectedModels.length > 1) {
            setDimensions('1:1');
        }
        else if (WAVESPEED_T2I_MODELS[model]) {
            setDimensions(WAVESPEED_T2I_MODELS[model].aspects[0]);
        }
        else if (model === 'Stable Diffusion 3.0' ||
            model === 'Stable Diffusion 3.5' ||
            model === 'Seedream 3.0' ||
            model === 'Seedream 4.0' ||
            model === 'Seedream 4.5' ||
            model === 'Seedream 5.0 Lite' ||
            model === 'Seedream 5.0 Pro' ||
            model === 'MiniMax Image 01' ||
            model === 'HunyuanImage 3.0' ||
            model === 'Bria 3.2' ||
            model === 'WAN 2.5 Preview' ||
            model === 'REVE' ||
            model === 'ImagineArt 1.5' ||
            model === 'Mai Image 2.5' ||
            model === 'Recraft v4.1' ||
            model === 'Recraft v4.1 Vector' ||
            model === 'Recraft v4.1 Pro' ||
            model === 'Recraft v4.1 Pro Vector' ||
            model === 'Nano Banana') {
            setDimensions('1:1');
        }
        else if (model === 'Midjourney V6' || model === 'Midjourney V7') {
            setNumberOfOutputs(4);
            setDimensions('1:1');
        }
        else if (model === 'FLUX.1' ||
            model === 'FLUX 1.1 Pro' ||
            model === 'FLUX 1.1 Pro Ultra' ||
            model === 'FLUX.2 [flex]' ||
            model === 'FLUX.2 [pro]' ||
            model === 'FLUX.2 [dev]' ||
            model === 'Z-Image Turbo') {
            setNumberOfOutputs(1);
            setDimensions('1:1');
        }
        else if (model === 'Ideogram v3' || model === 'Ideogram v4') {
            setDimensions('1:1');
        }
        else if (model === 'Flux Kontext Pro' ||
            model === 'Flux Kontext Max' ||
            model === 'Flux.1 Krea') {
            setNumberOfOutputs(1);
            setDimensions('1:1');
        }
        else if (isCustomModelSelected) {
            setDimensions('1:1');
        }
        else if ((action === 'Edit Image with AI' ||
            action === 'Caricature' ||
            action === 'Muppets' ||
            action === 'Ghiblify') &&
            totalUploadedImages > 0) {
            setNumberOfOutputs(1);
            // Prefer 'auto' (preserve original aspect ratio) when the model supports it
            if (dimensionsOptions.includes('auto')) {
                setDimensions('auto');
            }
            else if (originalImageDimensionsRef.current) {
                // Check if the auto-detected dimension is still valid for current model
                const isValidDimension = autoDetectedDimensionsRef.current &&
                    dimensionsOptions.includes(autoDetectedDimensionsRef.current);
                if (isValidDimension && autoDetectedDimensionsRef.current) {
                    // Use the previously detected dimension if it's still valid
                    setDimensions(autoDetectedDimensionsRef.current);
                }
                else {
                    // Re-calculate the closest ratio for the new model's options
                    const closestRatio = findClosestAspectRatio(originalImageDimensionsRef.current.width, originalImageDimensionsRef.current.height, dimensionsOptions);
                    if (closestRatio) {
                        autoDetectedDimensionsRef.current = closestRatio;
                        setDimensions(closestRatio);
                    }
                    else {
                        setDimensions('1:1');
                    }
                }
            }
            else if (autoDetectedDimensionsRef.current &&
                dimensionsOptions.includes(autoDetectedDimensionsRef.current)) {
                // Fallback: use auto-detected if available and valid (backwards compatibility)
                setDimensions(autoDetectedDimensionsRef.current);
            }
            else {
                setDimensions('1:1');
            }
        }
        else {
            // Reset auto-detected dimensions when no images are uploaded
            autoDetectedDimensionsRef.current = null;
            originalImageDimensionsRef.current = null;
            setDimensions('1:1');
        }
    }, [
        model,
        isCustomModelSelected,
        totalUploadedImages,
        action,
        editModel,
        selectedModels,
    ]);
    useEffect(() => {
        if (availableResolutions.length > 0) {
            const isEditing = action === 'Edit Image with AI' && totalUploadedImages > 0;
            const prefers1K = !isEditing && model === 'Nano Banana 2';
            const preferred = prefers1K ? '1K' : '2K';
            const defaultResolution = availableResolutions.includes(preferred)
                ? preferred
                : availableResolutions.includes('2K')
                    ? '2K'
                    : availableResolutions[0];
            setResolution((current) => current && availableResolutions.includes(current)
                ? current
                : defaultResolution);
        }
        else {
            setResolution(null);
        }
    }, [availableResolutions, model, editModel, action, totalUploadedImages]);
    useEffect(() => {
        if (dimensions !== '1024x1024' &&
            model !== 'Midjourney V6' &&
            model !== 'Midjourney V7' &&
            model !== 'Flux Kontext Pro' &&
            model !== 'Flux Kontext Max' &&
            model !== 'Seedream 3.0' &&
            model !== 'Seedream 4.0' &&
            model !== 'Seedream 4.5' &&
            model !== 'Seedream 5.0 Lite' &&
            model !== 'Seedream 5.0 Pro' &&
            model !== 'HunyuanImage 3.0' &&
            !WAVESPEED_T2I_MODELS[model] &&
            !isCustomModelSelected) {
            setNumberOfOutputs(1);
        }
    }, [dimensions]);
    const isPanoramaStyle = style === PANORAMA_STYLE_VALUE;
    useEffect(() => {
        if (!isPanoramaStyle)
            return;
        if (dimensions !== '2:1')
            setDimensions('2:1');
        if (selectedModels[0] !== DEFAULT_GPT_IMAGE_MODEL) {
            setSelectedModels([DEFAULT_GPT_IMAGE_MODEL] as ImageModelType[]);
        }
    }, [isPanoramaStyle, dimensions, selectedModels]);
    // Credits shown on the Generate button — MUST mirror the server-side
    // billing (the `used_credits` writes in /api/generate-image.ts and the
    // per-provider utils). Two mutually exclusive branches: a real edit
    // (uploads present) prices from `editModel` ALONE; everything else prices
    // from the selected text-to-image model ALONE. `action` defaults to
    // 'Edit Image with AI' and `editModel`/`selectedModels` keep their state
    // while their picker is hidden, so letting one branch's state leak into the
    // other's price makes the button disagree with the actual deduction
    // (customer-reported: button said 1 credit, 3 were billed).
    const calculateCreditsForModel = (modelName: ImageModelType): number => {
        const isCustomModel = trainingModels.some((tm) => tm.name === modelName);
        // AI Photo Studio: cost depends on the selected rendering engine
        if (isPhotoStudioModelSelected &&
            modelName === selectedTrainingModel?.name) {
            return (PHOTO_STUDIO_ENGINES.find((engine) => engine.value === photoStudioModel)
                ?.credits ?? 3);
        }
        // A real edit: price from the edit model + resolution only. The hidden
        // text-to-image selection is irrelevant here (the server routes and bills
        // by editModel), so it must never influence the shown price.
        if (action === 'Edit Image with AI' && totalUploadedImages > 0) {
            const nanoBananaEditCredits = getNanoBananaCredits(editModel, resolution);
            if (nanoBananaEditCredits !== null)
                return nanoBananaEditCredits;
            if (editModel === 'FLUX.2 [max]')
                return resolution === '2K' ? 5 : 2.5;
            if (editModel === 'FLUX.2 [flex]')
                return resolution === '2K' ? 6 : 3;
            if (editModel === 'Seedream 5.0 Pro')
                return resolution === '1K' ? 4 : 8;
            if (editModel === 'Grok Imagine 2.0')
                return resolution === '2K' ? 5 : 4;
            if (editModel === 'OpenAI GPT Image 1.5')
                return useHD ? 8 : 2;
            return editModels.find((m) => m.value === editModel)?.credits || 2;
        }
        // Text-to-image (or a non-edit action): price from the selected model +
        // action only; `editModel` must never leak in here.
        let modelCredits = modelName === 'Stable Diffusion 3.0' ||
            modelName === 'HiDream' ||
            modelName === 'Bria 3.2' ||
            modelName === 'WAN 2.2' ||
            modelName === 'WAN 2.5 Preview' ||
            modelName === 'Dreamina 3.1' ||
            modelName === 'Qwen-Image' ||
            modelName === 'Nano Banana' ||
            modelName === 'Stable Diffusion 3.5' ||
            modelName === 'Seedream 3.0' ||
            modelName === 'Seedream 4.0' ||
            modelName === 'Seedream 4.5' ||
            modelName === 'Seedream 5.0 Lite' ||
            modelName === 'REVE' ||
            modelName === 'ImagineArt 1.5' ||
            modelName === 'Mai Image 2.5'
            ? useHD
                ? 4
                : 2
            : action === 'Stylization'
                ? 16
                : action === 'Character Reference'
                    ? 8
                    : modelName === 'Midjourney V6' || modelName === 'Midjourney V7'
                        ? 2
                        : action === 'Remove Background'
                            ? 1
                            : (action === 'Muppets' ||
                                action === 'Ghiblify' ||
                                action === 'Caricature') &&
                                totalUploadedImages > 0 &&
                                useHD &&
                                !useLegacyEditModel
                                ? 8
                                : modelName === 'FLUX 1.1 Pro Ultra'
                                    ? 4
                                    : modelName === 'FLUX 1.1 Pro' ||
                                        modelName === 'Flux.1 Krea'
                                        ? 2
                                        : modelName === 'OpenAI GPT Image 1' &&
                                            useHD &&
                                            (dimensions === '1024x1536' ||
                                                dimensions === '1536x1024')
                                            ? 12
                                            : modelName === 'OpenAI GPT Image 1' &&
                                                (dimensions === '1024x1536' ||
                                                    dimensions === '1536x1024')
                                                ? 3
                                                : modelName === 'OpenAI GPT Image 1' && useHD
                                                    ? 8
                                                    : modelName === 'OpenAI GPT Image 1'
                                                        ? 2
                                                        : modelName === 'Ideogram v4'
                                                            ? 3
                                                            : modelName === 'Ideogram v3' && useHD
                                                                ? 5
                                                                : modelName === 'Ideogram v3' && !useHD
                                                                    ? 3
                                                                    : modelName === 'Recraft v4.1 Pro' ||
                                                                        modelName === 'Recraft v4.1 Pro Vector'
                                                                        ? 10
                                                                        : modelName === 'Recraft v4.1 Vector'
                                                                            ? 4
                                                                            : modelName === 'Recraft v4.1'
                                                                                ? 2
                                                                                : modelName === 'Recraft v3' &&
                                                                                    useVector
                                                                                    ? 4
                                                                                    : modelName === 'Recraft v3'
                                                                                        ? 2
                                                                                        : modelName === 'Flux Kontext Max'
                                                                                            ? 4
                                                                                            : modelName ===
                                                                                                'Flux Kontext Pro' &&
                                                                                                useHD
                                                                                                ? 4
                                                                                                : modelName ===
                                                                                                    'Flux Kontext Pro'
                                                                                                    ? 2
                                                                                                    : modelName ===
                                                                                                        'Stable Diffusion XL'
                                                                                                        ? 0.25
                                                                                                        : modelName ===
                                                                                                            'FLUX.1' && useHD
                                                                                                            ? 3
                                                                                                            : modelName ===
                                                                                                                'FLUX.1' && !useHD
                                                                                                                ? 0.25
                                                                                                                : isCustomModel
                                                                                                                    ? 2
                                                                                                                    : 1;
        if (modelName === 'HunyuanImage 3.0') {
            modelCredits = 5;
        }
        if (modelName === 'MiniMax Image 01') {
            modelCredits = 0.5;
        }
        if (action === 'Halloween') {
            modelCredits = 30;
        }
        // Nano Banana family (incl. Pro, whose 4K tier was previously shown at
        // the 1K price) — see @/constants/nanoBananaCredits.
        const nanoBananaCredits = getNanoBananaCredits(modelName, resolution);
        if (nanoBananaCredits !== null) {
            modelCredits = nanoBananaCredits;
        }
        if (modelName === 'FLUX.2 [max]') {
            modelCredits = resolution === '2K' ? 5 : 2.5;
        }
        if (modelName === 'Seedream 5.0 Pro') {
            modelCredits = resolution === '1K' ? 4 : 8;
        }
        if (modelName === 'FLUX.2 [flex]') {
            modelCredits = resolution === '2K' ? 6 : 3;
        }
        if (modelName === 'FLUX.2 [pro]') {
            modelCredits = 2;
        }
        if (modelName === 'FLUX.2 [dev]') {
            modelCredits = 1;
        }
        // Config-driven WaveSpeed t2i models — flat credits. (Actual edits never
        // reach this branch; they return early with editModel pricing above.)
        if (WAVESPEED_T2I_MODELS[modelName]) {
            modelCredits = WAVESPEED_T2I_MODELS[modelName].credits;
        }
        if (modelName === 'Z-Image Turbo') {
            modelCredits = resolution === '2K' ? 1 : 0.1;
        }
        if (modelName === 'Grok') {
            modelCredits = 1;
        }
        if (modelName === 'Grok Pro') {
            modelCredits = 4;
        }
        if (modelName === 'Grok Imagine 2.0') {
            modelCredits = resolution === '2K' ? 4 : 3;
        }
        if (modelName === 'Muse Image') {
            modelCredits = 1;
        }
        if (modelName === 'Seedream 4.0' ||
            modelName === 'Seedream 4.5' ||
            modelName === 'Seedream 5.0 Lite') {
            modelCredits = 2;
        }
        if (action === 'Visualize What Happens Next' && totalUploadedImages > 0) {
            modelCredits = 7;
        }
        if (modelName === 'Stable Diffusion XL') {
            modelCredits = 0.25;
        }
        if (modelName === 'FLUX.1') {
            modelCredits = useHD ? 3 : 0.25;
        }
        if (modelName === 'OpenAI GPT Image 1.5') {
            modelCredits = useHD ? 8 : 2;
        }
        if (modelName === 'OpenAI GPT Image 2 Low') {
            modelCredits = 0.5;
        }
        if (modelName === 'OpenAI GPT Image 2 Medium') {
            modelCredits = 3;
        }
        if (modelName === 'OpenAI GPT Image 2 High') {
            modelCredits = 11;
        }
        // GPT Image 2.5 Flare / Sunburst tiers bill the same as GPT Image 2's
        // matching quality tier (shared per-token pricing).
        const gptImage25Credits = modelName.startsWith('OpenAI GPT Image 2.5')
            ? getGptImage2FamilyCredits(modelName)
            : undefined;
        if (gptImage25Credits !== undefined) {
            modelCredits = gptImage25Credits;
        }
        return modelCredits;
    };
    // Calculate total credits for all selected models
    const credits = selectedModels.reduce((sum, modelName) => {
        return sum + calculateCreditsForModel(modelName);
    }, 0);
    const subscriptionName = ((subscription &&
        subscription?.prices?.products?.name) ||
        'Free') as SubscriptionName;
    const mutation = useMutation((prompt: Record<string, string | number | boolean>) => {
        return axiosInstance.post('/api/generate-image', prompt);
    });
    const clearInputs = () => {
        // Revoke all blob URLs before clearing
        newBeforeImages.forEach((imageUrl) => {
            if (imageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(imageUrl);
            }
        });
        newObjectImages.forEach((imageUrl) => {
            if (imageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(imageUrl);
            }
        });
        newUploadImages.forEach((imageUrl) => {
            if (imageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(imageUrl);
            }
        });
        setPrompt('');
        setStyle('None');
        setDimensions('1024x1024');
        setNewBeforeImages([]);
        setNewObjectImages([]);
        setNewUploadImages([]);
        setModerationStatus({});
        setEditModel(DEFAULT_GPT_IMAGE_MODEL);
        setSelectedModels([
            (trainingModel?.name as ImageModelType) ||
                (DEFAULT_GPT_IMAGE_MODEL as ImageModelType),
        ]);
        setCinematicState(DEFAULT_CINEMATIC_STATE);
    };
    // Update the email history check to use the same toast message pattern
    useEffect(() => {
        const emailHistory = JSON.parse(localStorage.getItem('emailHistory') || '[]');
        if (emailHistory.length >= 3 && isFreePlan) {
            setShouldUpgrade(true);
            setHasUsedTwoEmails(true);
        }
    }, []);
    // Cleanup blob URLs when component unmounts
    useEffect(() => {
        return () => {
            newBeforeImages.forEach((imageUrl) => {
                if (imageUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(imageUrl);
                }
            });
        };
    }, [newBeforeImages]);
    useEffect(() => {
        return () => {
            newObjectImages.forEach((imageUrl) => {
                if (imageUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(imageUrl);
                }
            });
        };
    }, [newObjectImages]);
    useEffect(() => {
        return () => {
            newUploadImages.forEach((imageUrl) => {
                if (imageUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(imageUrl);
                }
            });
        };
    }, [newUploadImages]);
    // Reset edit model if Flux Kontext is selected and more than 3 images are uploaded
    useEffect(() => {
        if (totalUploadedImages > 2 &&
            (editModel === 'Flux Kontext Pro' || editModel === 'Flux Kontext Max')) {
            // Switch to a model that supports multiple images
            setEditModel(DEFAULT_GPT_IMAGE_MODEL);
        }
    }, [totalUploadedImages, editModel]);
    // Handle saved prompt
    useEffect(() => {
        const savedPrompt = localStorage.getItem('savedImagePrompt');
        if (savedPrompt) {
            setPrompt(savedPrompt);
            // Clear the saved prompt after using it
            localStorage.removeItem('savedImagePrompt');
        }
    }, []); // Run only once on mount
    useEffect(() => {
        // Set action based on URL parameter if it exists
        if (router.query.action) {
            // Find the action in actionsWithDescription by ID
            const actionId = router.query.action as string;
            const foundAction = actionsWithDescription.find((action) => action.id === actionId);
            if (foundAction) {
                setAction(foundAction.title);
                // Set appropriate prompts based on the selected action
                if (foundAction.id === 'colorize') {
                    setPrompt('Colorize this image');
                }
                else if (foundAction.id === 'caricature') {
                    setPrompt('Caricature of this image');
                }
                else if (foundAction.id === 'halloween') {
                    setPrompt('Create 15 Halloween-themed photos from uploaded images');
                }
                else if (foundAction.id === 'ghiblify') {
                    setPrompt('Ghiblify this image');
                }
                else if (foundAction.id === 'muppets') {
                    setPrompt('Turn this image into a muppet');
                }
                else if (foundAction.id === 'visualize-what-happens-next') {
                    setPrompt('Visualize what happens next from this image');
                }
                else if (foundAction.id === 'stylization') {
                    setPrompt('transform to different styles');
                }
                else if (foundAction.id === 'realistic-photos') {
                    setPrompt('a man wearing a doctoral cap');
                }
                else if (foundAction.id === 'remove-background') {
                    setPrompt('Remove background from this image');
                }
                else if (foundAction.id === 'visualize-what-happens-next') {
                    setPrompt('Optional: Add context about the story or mood (e.g., "mysterious adventure", "happy celebration", "dramatic escape")');
                }
            }
        }
        // Depend on the specific primitive, not the full router.query object —
        // the object can have an unstable reference across renders (especially
        // around router.isReady), which causes setPrompt() to fire repeatedly
        // and exceed React's update-depth limit (#185) once it interacts with
        // ReactTextareaAutosize's layout effects.
    }, [router.query.action]);
    // Handle template URL params (prompt & image)
    useEffect(() => {
        if (!router.isReady)
            return;
        const promptParam = router.query.prompt;
        if (promptParam && typeof promptParam === 'string') {
            setPrompt(promptParam);
        }
        const imageParam = router.query.image;
        const image2Param = router.query.image2;
        if (imageParam && typeof imageParam === 'string') {
            const images = [imageParam];
            if (image2Param && typeof image2Param === 'string') {
                images.push(image2Param);
            }
            setNewBeforeImages(images);
        }
        // Load a reference as an Object (e.g. a product/design to place) instead of a Character
        const objectImageParam = router.query.objectImage;
        if (objectImageParam && typeof objectImageParam === 'string') {
            setNewObjectImages([objectImageParam]);
        }
    }, [
        router.isReady,
        router.query.prompt,
        router.query.image,
        router.query.image2,
        router.query.objectImage,
    ]);
    // Template the page was opened with — its card link's params are what
    // identify it (image templates have no id), shown in the ToolsNav card.
    const activeTemplate = useMemo(() => router.isReady
        ? findMediaTemplateForUrl('/ai-images', router.query)
        : undefined, 
    // asPath changes whenever the query does; router.query is not stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.isReady, router.asPath]);
    const removeTemplate = () => {
        if (!activeTemplate)
            return;
        const params = getMediaTemplateLinkParams(activeTemplate);
        // Undo only what the template filled in and the user hasn't changed since:
        // an edited prompt, the user's own photos or a model they picked stay.
        const templatePrompt = params.get('prompt');
        if (templatePrompt && prompt === templatePrompt)
            setPrompt('');
        const characterUrls = [params.get('image'), params.get('image2')].filter((url): url is string => !!url);
        if (characterUrls.length) {
            setNewBeforeImages((images) => images.filter((url) => !characterUrls.includes(url)));
        }
        const objectUrl = params.get('objectImage');
        if (objectUrl) {
            setNewObjectImages((images) => images.filter((url) => url !== objectUrl));
        }
        const templateModel = params.get('model');
        const templateModelTitle = modelsWithDescription.find((m) => m.title === templateModel || m.id === templateModel)?.title;
        if (templateModelTitle &&
            selectedModels.length === 1 &&
            selectedModels[0] === templateModelTitle) {
            setSelectedModels([DEFAULT_GPT_IMAGE_MODEL as ImageModelType]);
        }
        const templateEditModel = params.get('editModel');
        if (templateEditModel && editModel === templateEditModel) {
            setEditModel(DEFAULT_GPT_IMAGE_MODEL);
        }
        const query = { ...router.query };
        params.forEach((_, key) => {
            delete query[key];
        });
        void router.replace({ pathname: router.pathname, query }, undefined, {
            shallow: true,
            scroll: false,
        });
    };
    const templateCard = useMemo(() => {
        if (!activeTemplate)
            return null;
        // Newer templates are translated in media-templates, older ones only in
        // the page namespace (same lookup as the Templates tab).
        const tr = (key: string) => {
            const media = tMedia(key);
            return media !== key ? media : t(key);
        };
        return {
            title: tr(activeTemplate.title),
            previewUrl: activeTemplate.image,
            isVideoPreview: activeTemplate.isVideoPreview,
            sharePath: getMediaTemplateLandingPath(activeTemplate, router.locale),
        };
    }, [activeTemplate, tMedia, t, router.locale]);
    const resolveResolutionForModel = useCallback((modelName: ImageModelType) => {
        let availableResolutions: string[] = [];
        // If in edit mode with images uploaded, use edit model resolutions
        if (action === 'Edit Image with AI' && totalUploadedImages > 0) {
            const editModelInfo = editModels.find((item) => item.value === editModel);
            availableResolutions = editModelInfo?.resolutions || [];
        }
        else {
            // Otherwise use regular model resolutions
            const modelInfo = modelsWithDescription.find((item) => item.title === modelName);
            availableResolutions = modelInfo?.resolutions || [];
        }
        // Filter out 4K for free plans
        if (isFreePlan) {
            availableResolutions = availableResolutions.filter((r) => r !== '4K');
        }
        if (!availableResolutions.length) {
            return null;
        }
        const isEditing = action === 'Edit Image with AI' && totalUploadedImages > 0;
        const prefers1K = !isEditing && modelName === 'Nano Banana 2';
        const preferred = prefers1K ? '1K' : '2K';
        const preferredDefault = availableResolutions.includes(preferred)
            ? preferred
            : availableResolutions.includes('2K')
                ? '2K'
                : availableResolutions[0];
        if (resolution && availableResolutions.includes(resolution)) {
            return resolution;
        }
        return preferredDefault;
    }, [resolution, action, totalUploadedImages, editModel, isFreePlan]);
    async function submit(e: React.SyntheticEvent) {
        e.preventDefault();
        if (!user) {
            setShowAuth(true);
            return;
        }
        // if mutation is loading, return
        if (mutation.isLoading || pendingImageCount > 0) {
            return;
        }
        // check if prompt is empty
        if (!prompt) {
            return toast.error('Please enter a prompt');
        }
        // Check if any images are still blob URLs (not yet uploaded)
        const hasBlobUrls = newBeforeImages.some((url) => url?.startsWith('blob:'));
        if (hasBlobUrls) {
            return toast.error('Please wait for all images to finish uploading before generating');
        }
        // Confirmation for Halloween action
        if (action === 'Halloween') {
            const confirmed = window.confirm('This will generate 15 Halloween-themed images and cost 30 credits. Do you want to continue?');
            if (!confirmed) {
                return;
            }
        }
        if (isFreePlan && numberOfOutputs > 1) {
            toast.info(<div>
          <div className='mb-5'>
            {t('Multiple image generation is only available in Pro plans.')}
          </div>
          <ButtonLink variant='dark' href='/settings/billing'>
            {t('Upgrade plan')}
          </ButtonLink>
        </div>);
            return;
        }
        if (isFreePlan && action === 'Stylization') {
            setShouldUpgrade(true);
            return toast.info('Stylization is only available in Pro plans. Please upgrade to use this feature.');
        }
        if (isFreePlan &&
            (model === 'Midjourney V6' || model === 'Midjourney V7')) {
            setShouldUpgrade(true);
            return toast.info('Midjourney V6 model is only available in Pro plans. Please upgrade to use this model.');
        }
        if (isFreePlan && resolution === '4K') {
            setShouldUpgrade(true);
            return toast.info('4K resolution is only available in Pro plans. Please upgrade to use this feature.');
        }
        setActiveTab('images');
        const generationStartedAt = Date.now();
        setPendingGeneration({
            count: selectedModels.length * numberOfOutputs,
            startedAt: generationStartedAt,
        });
        try {
            // Call API for all selected models in parallel
            const allResponses = await Promise.all(selectedModels.map((currentModel) => {
                const modelResolution = resolveResolutionForModel(currentModel);
                const cinematicSuffix = buildCinematicPrompt(cinematicState ?? DEFAULT_CINEMATIC_STATE);
                const payload: Record<string, string | number | boolean> = {
                    prompt: prompt + cinematicSuffix,
                    style,
                    recraftStyle,
                    dimensions,
                    outputs: numberOfOutputs,
                    model: currentModel,
                    useHD,
                    images: JSON.stringify([
                        ...newBeforeImages,
                        ...newObjectImages,
                        ...newUploadImages,
                    ]),
                    action,
                    strength,
                    promptUpsampling,
                    searchPrompt,
                    useVector,
                    useRawMode,
                    useLegacyEditModel,
                    editModel,
                    photoStudioModel,
                };
                if (modelResolution) {
                    payload.resolution = modelResolution;
                }
                return mutation.mutateAsync(payload);
            }));
            // increment total images
            setLocalTotalImages((total) => total + credits * numberOfOutputs);
            // Track generation count for subscription prompt
            const currentGenerationCount = generationCount ?? 0;
            setGenerationCount(currentGenerationCount + 1);
            // Aggregate all new images from all models
            // For free users on 2nd+ generation, add queue timestamp to show prompt even for sync models
            const shouldQueueImages = FREE_MEDIA_QUEUE_ENABLED && isFreePlan && currentGenerationCount >= 1;
            const queuedUntil = shouldQueueImages
                ? Date.now() + 45 * 60 * 1000 // 45 minutes from now
                : undefined;
            allResponses.forEach((response) => {
                response.data.forEach((newImage: ImageType) => {
                    newImageStartTimes.current.set(String(newImage.id), generationStartedAt);
                });
            });
            setGeneratedImages((images) => {
                const allNewImages = allResponses.flatMap((response) => response.data
                    .filter((newImage: ImageType) => !images.some((existingImage) => existingImage.id === newImage.id))
                    .map((newImage: ImageType) => ({
                    ...newImage,
                    _queuedUntil: queuedUntil,
                })));
                return [...allNewImages, ...images];
            });
            setHasProcessingImages(true);
            pollingGraceUntilRef.current = Date.now() + 15000;
            client.invalidateQueries(['image-generator-images']);
            // scroll to top
            generatedCaptions.current &&
                generatedCaptions.current.scrollIntoView({ behavior: 'smooth' });
        }
        catch (error: any) {
            if (error?.response?.data?.error &&
                typeof error.response.data.error === 'string' &&
                error.response.data.error.includes(' reached ')) {
                analytics.track('Words limit reached', {
                    page: 'Images',
                });
                setShouldUpgrade(true);
            }
            else if (error?.response?.data?.error?.message) {
                toast.error(error?.response?.data?.error?.message);
            }
            else if (error?.response?.data?.error) {
                toast.error('Something went wrong. Please try again later. If the error persists please contact us at support@easy-peasy.ai or via chat.');
            }
            // }
        }
        finally {
            setPendingGeneration(null);
        }
    }
    const apiGenerateMutation = useMutation((prompt: Record<string, string>) => {
        return axiosInstance.post('/api/generate', prompt);
    });
    const improvePrompt = async () => {
        if (!user) {
            setShowAuth(true);
            return;
        }
        try {
            const promptString = isCustomModelSelected && !prompt
                ? `a photo of a ${selectedTrainingModel?.gender} person`
                : prompt;
            const response = await apiGenerateMutation.mutateAsync({
                preset: 'art-prompt-generator',
                keywords: promptString || '<random>',
            });
            const { data } = response;
            setPrompt(data[0].text.replace(/person|man|woman|girl|boy/gi, 'model'));
        }
        catch (error) {
            toastError(error);
        }
    };
    const moderateImage = async (imageUrl: string) => {
        try {
            setModerationStatus((prev) => ({
                ...prev,
                [imageUrl]: { flagged: false, loading: true },
            }));
            const response = await axiosInstance.post('/api/moderate-image', {
                imageUrl: imageUrl,
            });
            const { flagged } = response.data;
            setModerationStatus((prev) => ({
                ...prev,
                [imageUrl]: { flagged, loading: false },
            }));
            return { flagged };
        }
        catch (error) {
            console.error('Error moderating image:', error);
            // If moderation fails, allow the image (fail silently)
            setModerationStatus((prev) => ({
                ...prev,
                [imageUrl]: { flagged: false, loading: false },
            }));
            return { flagged: false };
        }
    };
    // Helper function to find the closest aspect ratio match
    const findClosestAspectRatio = (imageWidth: number, imageHeight: number, availableOptions: string[]): string | null => {
        const uploadedRatio = imageWidth / imageHeight;
        // Parse dimension option to get its aspect ratio
        const parseRatio = (option: string): number | null => {
            if (option === 'auto' || option === 'match_input_image') {
                return null; // Skip auto options
            }
            // Handle ratio format (e.g., "16:9", "1:1")
            if (option.includes(':')) {
                const [w, h] = option.split(':').map(Number);
                return w && h ? w / h : null;
            }
            // Handle pixel format (e.g., "1024x1024", "1792x1024")
            if (option.includes('x')) {
                const [w, h] = option.split('x').map(Number);
                return w && h ? w / h : null;
            }
            return null;
        };
        let closestOption: string | null = null;
        let smallestDifference = Infinity;
        for (const option of availableOptions) {
            const optionRatio = parseRatio(option);
            if (optionRatio === null)
                continue;
            const difference = Math.abs(uploadedRatio - optionRatio);
            if (difference < smallestDifference) {
                smallestDifference = difference;
                closestOption = option;
            }
        }
        return closestOption;
    };
    const handleFileUpload = async (inputFile: File, setter: React.Dispatch<React.SetStateAction<string[]>> = setNewBeforeImages, kind: 'character' | 'object' | 'image' = 'character') => {
        // upload to Supabase
        setIsFileLoading(true);
        if (!inputFile) {
            setIsFileLoading(false);
            return;
        }
        // Downscale/re-encode oversized images so large phone/camera photos
        // upload quickly instead of being rejected for exceeding the size cap.
        const file = await compressImage(inputFile, {
            force: true,
            format: 'jpeg',
        });
        // Validate image dimensions (max 7000px width or height)
        let imageDimensions: {
            width: number;
            height: number;
        };
        try {
            imageDimensions = await new Promise<{
                width: number;
                height: number;
            }>((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    resolve({ width: img.width, height: img.height });
                    URL.revokeObjectURL(img.src);
                };
                img.onerror = reject;
                img.src = URL.createObjectURL(file);
            });
            if (imageDimensions.width > 7000 || imageDimensions.height > 7000) {
                toast.error(`Image "${file.name}" exceeds the maximum allowed dimensions of 7000px (${imageDimensions.width}x${imageDimensions.height})`);
                setIsFileLoading(false);
                return;
            }
            // Store original image dimensions for re-calculation when model changes
            originalImageDimensionsRef.current = {
                width: imageDimensions.width,
                height: imageDimensions.height,
            };
            // Auto-detect and store the closest aspect ratio in ref
            const closestRatio = findClosestAspectRatio(imageDimensions.width, imageDimensions.height, dimensionsOptions);
            if (closestRatio) {
                autoDetectedDimensionsRef.current = closestRatio;
            }
        }
        catch (error) {
            console.error('Error validating image dimensions:', error);
            toast.error('Failed to validate image dimensions. Please try again.');
            setIsFileLoading(false);
            return;
        }
        const blobUrl = URL.createObjectURL(file);
        setter((images) => [...images, blobUrl]);
        try {
            const upload = await uploadUserImage(file, kind);
            const publicUrl = upload.image_url;
            setter((images) => images.map((img) => (img === blobUrl ? publicUrl : img)));
            URL.revokeObjectURL(blobUrl);
            client.invalidateQueries({ queryKey: ['user-uploads', kind] });
            client.invalidateQueries({ queryKey: ['user-uploads', 'all'] });
            moderateImage(publicUrl);
        }
        catch (error) {
            console.error('Upload error', error);
            toast.error(t('Failed to upload image. Please try again.'));
            setter((images) => images.filter((img) => img !== blobUrl));
            URL.revokeObjectURL(blobUrl);
        }
        finally {
            setIsFileLoading(false);
        }
    };
    const handleMicrophoneAudioReady = async (audio: Blob) => {
        const fileSizeInMB = audio.size / (1024 * 1024);
        setIsTranscribing(true);
        const getFileExtension = (mimeType: string) => {
            const mimeMap: {
                [key: string]: string;
            } = {
                'audio/wav': 'wav',
                'audio/mpeg': 'mp3',
                'audio/mp4': 'mp4',
                'audio/m4a': 'm4a',
                'audio/webm': 'webm',
                'audio/ogg': 'ogg',
            };
            return mimeMap[mimeType] || 'wav';
        };
        const fileExtension = getFileExtension(audio.type);
        const fileName = `transcription-${Date.now()}.${fileExtension}`;
        try {
            let transcriptionResponse;
            if (fileSizeInMB > 4) {
                const { error: uploadError } = await supabaseClient.storage
                    .from('audios')
                    .upload(`${user?.id || 'guest'}/${fileName}`, audio, {
                    contentType: audio.type,
                });
                if (uploadError)
                    throw uploadError;
                const { data: { publicUrl }, } = supabaseClient.storage
                    .from('audios')
                    .getPublicUrl(`${user?.id || 'guest'}/${fileName}`);
                const formData = new FormData();
                formData.append('audioUrl', publicUrl);
                formData.append('language', '');
                transcriptionResponse = await fetch('/api/transcribe', {
                    method: 'POST',
                    body: formData,
                });
            }
            else {
                const formData = new FormData();
                formData.append('audio', audio);
                formData.append('language', '');
                transcriptionResponse = await fetch('/api/transcribe', {
                    method: 'POST',
                    body: formData,
                });
            }
            if (!transcriptionResponse.ok) {
                throw new Error('Transcription failed');
            }
            const data = await transcriptionResponse.json();
            setPrompt((prev) => prev + data.transcription);
            analytics?.track('Audio Transcribed', {
                transcription: data.transcription,
                page: window.location.pathname,
                size: fileSizeInMB,
            });
            if (fileSizeInMB > 4) {
                await supabaseClient.storage
                    .from('audios')
                    .remove([`${user?.id || 'guest'}/${fileName}`]);
            }
        }
        catch (error) {
            console.error('Error transcribing audio:', error);
            toast.error('Failed to transcribe audio. Please try again.');
        }
        finally {
            setIsTranscribing(false);
            setShowMicrophoneModal(false);
        }
    };
    const handleAcceptedFiles = useCallback((acceptedFiles: File[], setter: React.Dispatch<React.SetStateAction<string[]>> = setNewBeforeImages, kind: 'character' | 'object' | 'image' = 'character') => {
        if (!user) {
            setShowAuth(true);
            return;
        }
        // NOTE(2026-07-23): the free-plan upload block below is DISABLED —
        // free users can now upload images. Rationale: uploads are ~free (R2),
        // every upload runs /api/moderate-image, generation cost is already
        // gated by credits, and /ai-videos lets free users upload
        // reference/start images, so this gate was inconsistent with the free
        // video tier. (The `isFromDevelopedCountry` carve-out inside was dead
        // code even before — the block ignored it.) Restore by uncommenting and
        // re-importing DEVELOPED_COUNTRIES from '@/constants/common'.
        // const isFromDevelopedCountry =
        //   country && DEVELOPED_COUNTRIES.includes(country);
        //
        // if (isFreePlan) {
        //   toast.info(
        //     <div>
        //       <div className='mb-5'>
        //         {t(
        //           'This feature is only available in Pro plans. Please upgrade to use this feature.',
        //         )}
        //       </div>
        //       <ButtonLink variant='dark' href='/settings/billing'>
        //         {t('Upgrade plan')}
        //       </ButtonLink>
        //     </div>,
        //   );
        //   return;
        // }
        if (acceptedFiles.length === 0)
            return;
        const MAX_TOTAL = 8;
        const currentTotal = newBeforeImages.length +
            newObjectImages.length +
            newUploadImages.length;
        const slotsLeft = MAX_TOTAL - currentTotal;
        if (slotsLeft <= 0) {
            toast.error(t('You can upload up to {{max}} images total.', { max: MAX_TOTAL }));
            return;
        }
        const filesToUpload = acceptedFiles.slice(0, slotsLeft);
        if (acceptedFiles.length > slotsLeft) {
            toast.info(t('Only the first {{count}} image(s) were added — the limit is {{max}} total.', { count: slotsLeft, max: MAX_TOTAL }));
        }
        filesToUpload.forEach((file) => handleFileUpload(file, setter, kind));
    }, [
        isFreePlan,
        country,
        t,
        user,
        newBeforeImages,
        newObjectImages,
        newUploadImages,
    ]);
    const onDrop = useCallback((acceptedFiles: File[]) => handleAcceptedFiles(acceptedFiles), [handleAcceptedFiles]);
    const removeUploadedImage = (url: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
        setter((imgs) => imgs.filter((i) => i !== url));
        if (url.startsWith('blob:'))
            URL.revokeObjectURL(url);
    };
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/webp': ['.webp'],
        },
        maxFiles: 10,
        multiple: true,
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingImageUrl, setEditingImageUrl] = useState<string | null>(null);
    const allUploadedImages = [
        ...newBeforeImages,
        ...newObjectImages,
        ...newUploadImages,
    ];
    // Get dimensions for the selected model
    const dimensionsOptions = getDimensionsForModel({
        model,
        action,
        editModel,
        newBeforeImages: allUploadedImages,
        selectedModels,
    });
    // Handle "Regenerate" deep link: pre-populate the form from a previous image.
    // The source params are stashed in sessionStorage by ImageOutput before
    // navigation (same pattern as the ai-videos regenerate flow).
    const appliedRegenerateRef = useRef<string | null>(null);
    // Dimensions/resolution can't be applied in the same commit as the model or
    // source-image change — the model-defaults effect above would reset them on
    // the following render. Stash them here and let the effect below (declared
    // after the defaults effects, so it runs later in the same commit) apply
    // them once the model/images state has landed.
    const pendingRegenerateRef = useRef<{
        model?: string;
        hasImages?: boolean;
        dimensions?: string;
        resolution?: string;
    } | null>(null);
    useEffect(() => {
        if (!router.isReady)
            return;
        const regenerateKey = router.query.regenerate;
        if (!regenerateKey || typeof regenerateKey !== 'string')
            return;
        if (appliedRegenerateRef.current === regenerateKey)
            return;
        appliedRegenerateRef.current = regenerateKey;
        let raw: string | null = null;
        try {
            raw = sessionStorage.getItem('regenerate-image');
        }
        catch {
            raw = null;
        }
        if (!raw)
            return;
        try {
            sessionStorage.removeItem('regenerate-image');
        }
        catch {
            // ignore storage errors
        }
        let data: any;
        try {
            data = JSON.parse(raw);
        }
        catch {
            return;
        }
        if (data.prompt) {
            setPrompt(String(data.prompt));
        }
        const pending: {
            model?: string;
            hasImages?: boolean;
            dimensions?: string;
            resolution?: string;
        } = {
            dimensions: typeof data.dimensions === 'string' && data.dimensions
                ? data.dimensions
                : undefined,
            resolution: typeof data.resolution === 'string' && data.resolution
                ? data.resolution
                : undefined,
        };
        // Restore source images for edit-flow regenerations
        const sourceImages = Array.isArray(data.images)
            ? data.images.filter((url: any) => typeof url === 'string' && url)
            : [];
        if (sourceImages.length > 0) {
            setNewBeforeImages(sourceImages);
            pending.hasImages = true;
        }
        if (data.model && typeof data.model === 'string') {
            if (sourceImages.length > 0) {
                // Edit flow: the row's model is an edit model
                const isValidEditModel = editModels.some((m) => m.value === data.model);
                if (isValidEditModel &&
                    !(isFreePlan && isPremiumEditModel(data.model))) {
                    setEditModel(data.model);
                }
            }
            else {
                const foundTrainingModel = trainingModels.find((tm) => tm.name === data.model);
                const standardModel = modelsWithDescription.find((m) => m.title === data.model);
                if (foundTrainingModel) {
                    if (model !== data.model) {
                        setSelectedModels([data.model as ImageModelType]);
                        pending.model = data.model;
                    }
                    setSelectedTrainingModel(foundTrainingModel);
                }
                else if (standardModel &&
                    !(isFreePlan && isPremiumImageModel(standardModel.title))) {
                    if (model !== standardModel.title) {
                        setSelectedModels([standardModel.title as ImageModelType]);
                        pending.model = standardModel.title;
                    }
                    setSelectedTrainingModel(null);
                }
            }
        }
        pendingRegenerateRef.current = pending;
        // `model` is deliberately read, not depended on — this effect must fire
        // once per regenerate key, not when the selection changes afterwards.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady, router.query.regenerate, trainingModels, isFreePlan]);
    // Apply the stashed dimensions/resolution once the restored model/images
    // have landed. Runs every render (guarded by the ref) so it fires in the
    // same commit as — and after — the model-defaults resets above.
    useEffect(() => {
        const pending = pendingRegenerateRef.current;
        if (!pending)
            return;
        // Wait until the state set by the regenerate effect has actually landed
        if (pending.model && model !== pending.model)
            return;
        if (pending.hasImages && totalUploadedImages === 0)
            return;
        pendingRegenerateRef.current = null;
        if (pending.dimensions && dimensionsOptions.includes(pending.dimensions)) {
            setDimensions(pending.dimensions);
        }
        if (pending.resolution &&
            availableResolutions.includes(pending.resolution)) {
            setResolution(pending.resolution);
        }
        // dimensionsOptions is a fresh array every render, so this effectively
        // runs each render — the pendingRegenerateRef guard makes that a no-op
        // after the one application.
    }, [model, totalUploadedImages, dimensionsOptions, availableResolutions]);
    const hasUploadingImage = allUploadedImages.some((url) => url?.startsWith('blob:'));
    const hasFlaggedUpload = allUploadedImages.some((url) => moderationStatus[url]?.flagged);
    return (<NarrowDashboardLayout showMobileMenu={true}>
      <main className='flex-1 overflow-y-auto focus:outline-none'>
        <Seo templateTitle={t('AI Image Generator')}/>
        {shouldUpgrade && (<PricingModal isVisible={shouldUpgrade} isImages={true}/>)}

        <main className='z-10 mx-auto flex flex-col lg:h-[calc(100svh-env(safe-area-inset-bottom))] lg:flex-row lg:overflow-y-hidden'>
          <div className='pg-scrollbar left-0 z-20 flex w-full translate-x-0 transform flex-col border-r border-gray-200 bg-white transition-transform duration-150 dark:border-white/5 dark:bg-slate-900 lg:fixed lg:ml-20 lg:h-[calc(100svh-env(safe-area-inset-bottom))] lg:w-1/3 lg:!overflow-x-hidden lg:overflow-y-auto lg:max-w-[480px] lg:min-w-[288px]'>
            <EditModal image={{ image_url: editingImageUrl || '' }} isOpen={showEditModal} onClose={() => {
            setShowEditModal(false);
            setEditingImageUrl(null);
        }} onUpdate={(updatedImage) => {
            const newUrl = updatedImage.image_url;
            if (!newUrl || !editingImageUrl)
                return;
            const replaceInList = (setter: React.Dispatch<React.SetStateAction<string[]>>) => setter((images) => images.map((u) => (u === editingImageUrl ? newUrl : u)));
            replaceInList(setNewBeforeImages);
            replaceInList(setNewObjectImages);
            replaceInList(setNewUploadImages);
            setEditingImageUrl(newUrl);
        }}/>
            <ToolsNav activeToolSlug='ai-images' template={templateCard && {
            ...templateCard,
            onBrowseTemplates: () => setActiveTab('templates'),
            onRemove: removeTemplate,
        }} actions={user ? (<CreditsDisplay totalUsed={localTotalImages} totalAvailable={imagesInPlan} bonusCredits={bonusImages} subscriptionName={subscriptionName} isFreePlan={isFreePlan} type='images' showBuyButton={false}/>) : undefined}/>
            <form onSubmit={submit} className='relative h-full flex flex-col'>
              <div className='flex-1 px-4 py-8 pb-16 dark:divide-slate-200/5 sm:p-6 sm:pt-0'>
                <div>
                  <div className='grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6'>
                    {/* Show image upload section first if action parameter exists */}
                    {hasActionParam && (<div className='sm:col-span-6'>
                        <label htmlFor='upscale_image' className='block text-sm font-medium text-gray-700 dark:text-slate-400'>
                          {t('Upload Image')}
                        </label>
                        <div className='mt-1 flex items-center justify-center'>
                          <div {...getRootProps()} className={clsxm('flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-6 text-sm font-medium transition-colors', isDragActive
                ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-slate-700/40'
                : 'border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700')}>
                            {isFileLoading ? (<Spinner className='animate-spin-fast m-1 h-10 w-10 cursor-pointer text-primary-500'/>) : (<>
                                <PhotographIcon className='mb-3 h-10 w-10 text-gray-400'/>
                                <p className='mb-1 text-base'>
                                  {newBeforeImages.length > 0
                    ? t(`${newBeforeImages.length} image${newBeforeImages.length > 1 ? 's' : ''} uploaded`)
                    : t('Upload Image')}
                                </p>
                                <p className='text-xs text-gray-500 dark:text-gray-400'>
                                  {isDragActive
                    ? t('Drop the file here...')
                    : newBeforeImages.length > 0
                        ? t('Click or drag to add more images')
                        : t('Drag and drop or click to select multiple images')}
                                </p>
                                <input {...getInputProps()} id='upscale_image' disabled={isFileLoading}/>
                              </>)}
                          </div>
                        </div>
                      </div>)}

                    {newBeforeImages.some((imageUrl) => moderationStatus[imageUrl]?.flagged) && (<div className='sm:col-span-6'>
                        <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800'>
                          <div className='flex items-center'>
                            <span className='text-red-600 dark:text-red-400 text-sm font-medium'>
                              ⚠️ {t('Content Policy Violation')}
                            </span>
                          </div>
                          <p className='text-red-600 dark:text-red-400 text-xs mt-1'>
                            {t('One or more uploaded images violate our content policy. Please remove the flagged images to continue.')}
                          </p>
                        </div>
                      </div>)}

                    <div className='sm:col-span-6'>
                      <Modal showModal={showMicrophoneModal} onClose={() => setShowMicrophoneModal(false)} className='p-20'>
                        <AudioRecorder onAudioAvailable={handleMicrophoneAudioReady} isLoading={isTranscribing} maxRecordDuration={600} startRecordingImmediately={true}/>
                      </Modal>
                      <label htmlFor='prompt' className='block text-sm font-medium text-gray-700 dark:text-slate-400'>
                        {action === 'Find and Replace'
            ? t('Describe the part of your image you want to modify.')
            : t('Prompt')}
                      </label>
                      <div className='relative mt-2 flex rounded-md shadow-sm'>
                        <ReactTextareaAutosize rows={5} minRows={5} maxRows={10} onChange={(e) => setPrompt(e.target.value)} name='prompt' id='prompt' disabled={apiGenerateMutation.isLoading} value={prompt} placeholder={action === 'Visualize What Happens Next'
            ? t('Optional: Add context about the story or mood (e.g., "mysterious adventure", "happy celebration", "dramatic escape")')
            : isCustomModelSelected
                ? t('Model in style of Picasso, oil on canvas, cubism style painting')
                : t('e.g., a majestic lion in sunset, futuristic city skyline, abstract watercolor portrait...')} className='block pb-10 w-full min-w-0 flex-1 rounded border-gray-300 focus:border-primary-500 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50 dark:border-white/5 dark:bg-slate-700/50 dark:text-white sm:text-sm'/>
                        {/* Bottom right - Dictate and Improve/Random prompt buttons */}
                        <div className='absolute bottom-1 right-1 flex items-center gap-1'>
                          <SimpleTooltip title={t('Dictate')}>
                            <button type='button' onClick={(e) => {
            e.preventDefault();
            setShowMicrophoneModal(true);
        }} className='p-1.5 rounded-md text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'>
                              <MicrophoneIcon className='h-5 w-5'/>
                            </button>
                          </SimpleTooltip>
                          <SimpleTooltip title={prompt.length
            ? t('Improve prompt')
            : t('Random prompt')}>
                            <button type='button' onClick={(e) => {
            e.preventDefault();
            improvePrompt();
        }} disabled={apiGenerateMutation.isLoading} className='p-1.5 rounded-md text-gray-500 hover:text-primary-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'>
                              {apiGenerateMutation.isLoading ? (<Spinner className='h-5 w-5 animate-spin text-primary-500'/>) : prompt.length ? (<SparklesIcon className='h-5 w-5'/>) : (<CubeIcon className='h-5 w-5'/>)}
                            </button>
                          </SimpleTooltip>
                        </div>
                      </div>
                      {isCustomModelSelected && (<p className='mt-1 text-xs text-gray-500 dark:text-slate-400'>
                          {t('Include "Model" in your prompt to activate the trained concept.')}
                        </p>)}
                      {action === 'Find and Replace' && (<div className='mt-4'>
                          <label htmlFor='search_prompt' className='block text-sm font-medium text-gray-700 dark:text-slate-400'>
                            {t('Describe what you want to replace it with.')}
                          </label>
                          <div className='mt-1'>
                            <textarea id='search_prompt' name='search_prompt' rows={3} className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white sm:text-sm' placeholder={t('E.g. "chair"')} value={searchPrompt} onChange={(e) => setSearchPrompt(e.target.value)}/>
                          </div>
                        </div>)}
                    </div>

                    <div className='sm:col-span-6'>
                      <label className='block text-sm font-medium text-gray-700 dark:text-slate-400'>
                        {t('Customize')}
                      </label>
                      <div className='mt-2 grid grid-cols-[repeat(auto-fit,minmax(88px,1fr))] gap-3'>
                        <OptionCard label={t('Upload')} selected={false} onClick={() => {
            if (!user) {
                setShowAuth(true);
                return;
            }
            uploadFileInputRef.current?.click();
        }} icon={<CloudUploadIcon />} showAddBadge/>
                        {newUploadImages.map((imageUrl, index) => (<UploadedImageCard key={imageUrl} imageUrl={imageUrl} label={`image${index + 1}`} onDelete={() => removeUploadedImage(imageUrl, setNewUploadImages)} onEdit={imageUrl?.startsWith('blob:')
                ? undefined
                : () => {
                    setEditingImageUrl(imageUrl);
                    setShowEditModal(true);
                }}/>))}
                        <OptionCard label={t('Character')} selected={false} onClick={() => {
            if (!user) {
                setShowAuth(true);
                return;
            }
            setSceneModalTab('character');
        }} icon={<UserIcon />} showAddBadge/>
                        {newBeforeImages.map((imageUrl, index) => (<UploadedImageCard key={imageUrl} imageUrl={imageUrl} label={`character${index + 1}`} onDelete={() => removeUploadedImage(imageUrl, setNewBeforeImages)} onEdit={imageUrl?.startsWith('blob:')
                ? undefined
                : () => {
                    setEditingImageUrl(imageUrl);
                    setShowEditModal(true);
                }}/>))}
                        <OptionCard label={t('Object')} selected={false} onClick={() => {
            if (!user) {
                setShowAuth(true);
                return;
            }
            setSceneModalTab('object');
        }} icon={<CubeIcon />} showAddBadge/>
                        {newObjectImages.map((imageUrl, index) => (<UploadedImageCard key={imageUrl} imageUrl={imageUrl} label={`object${index + 1}`} onDelete={() => removeUploadedImage(imageUrl, setNewObjectImages)} onEdit={imageUrl?.startsWith('blob:')
                ? undefined
                : () => {
                    setEditingImageUrl(imageUrl);
                    setShowEditModal(true);
                }}/>))}
                        <OptionCard label={style !== 'None' ? t(style) : t('Style')} selected={style !== 'None'} onClick={() => setSceneModalTab('style')} icon={<StarIcon />} emoji={style !== 'None'
            ? STYLE_OPTIONS.find((s) => s.value === style)
                ?.emoji
            : undefined} previewUrl={style !== 'None'
            ? STYLE_OPTIONS.find((s) => s.value === style)
                ?.previewUrl
            : undefined} onClear={() => setStyle('None')}/>
                        <OptionCard label={(() => {
            const preset = findMatchingPreset(cinematicState?.camera ?? 'Standard', cinematicState?.lens ?? 'Standard', cinematicState?.focalLength ?? 'Standard', cinematicState?.aperture ?? 'Standard');
            if (preset)
                return preset.label;
            return (cinematicState?.camera ?? 'Standard') !==
                'Standard'
                ? (cinematicState?.camera as string)
                : t('Camera');
        })()} selected={(cinematicState?.camera ?? 'Standard') !==
            'Standard'} onClick={() => setSceneModalTab('camera')} icon={<CameraIcon />} previewUrl={findMatchingPreset(cinematicState?.camera ?? 'Standard', cinematicState?.lens ?? 'Standard', cinematicState?.focalLength ?? 'Standard', cinematicState?.aperture ?? 'Standard')?.previewUrl ??
            CAMERA_OPTIONS.find((o) => o.label === cinematicState?.camera)?.previewUrl} onClear={() => setCinematicState({
            ...(cinematicState ?? DEFAULT_CINEMATIC_STATE),
            camera: 'Standard',
            lens: 'Standard',
            focalLength: 'Standard',
            aperture: 'Standard',
        })}/>
                        <OptionCard label={(cinematicState?.lighting ?? 'None') !== 'None'
            ? (cinematicState?.lighting as string)
            : t('Lighting')} selected={(cinematicState?.lighting ?? 'None') !== 'None'} onClick={() => setSceneModalTab('lighting')} icon={<LightBulbIcon />} previewUrl={LIGHTING_OPTIONS.find((o) => o.label === cinematicState?.lighting)?.previewUrl} onClear={() => setCinematicState({
            ...(cinematicState ?? DEFAULT_CINEMATIC_STATE),
            lighting: 'None',
        })}/>
                        <OptionCard label={(cinematicState?.shotType ?? 'None') !== 'None'
            ? (cinematicState?.shotType as string)
            : t('Shot Type')} selected={(cinematicState?.shotType ?? 'None') !== 'None'} onClick={() => setSceneModalTab('shot-type')} icon={<FilmIcon />} previewUrl={SHOT_TYPE_OPTIONS.find((o) => o.label === cinematicState?.shotType)?.previewUrl} onClear={() => setCinematicState({
            ...(cinematicState ?? DEFAULT_CINEMATIC_STATE),
            shotType: 'None',
        })}/>
                        <OptionCard label={t('packs.customize')} selected={false} onClick={() => setShowPhotoPacks(true)} icon={<CollectionIcon />}/>
                      </div>
                      <input ref={uploadFileInputRef} type='file' accept='image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp' multiple className='sr-only' disabled={isFileLoading} onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) {
                handleAcceptedFiles(Array.from(files), setNewUploadImages, 'image');
            }
            e.target.value = '';
        }}/>
                    </div>

                    {action === 'Realistic Photos' &&
            totalUploadedImages > 0 && (<div className='mt-5 sm:col-span-6'>
                          <label htmlFor='style' className='block text-sm font-medium text-gray-700 dark:text-slate-400'>
                            {t('Style')}
                          </label>
                          <div className='mt-1 flex rounded-md'>
                            <Select onChange={(e) => setStyle(e)} label='style' value={style} options={[
                {
                    value: 'None',
                    label: 'None',
                },
                {
                    value: 'Photographic (Default)',
                    label: 'Photographic (Default)',
                },
                { value: 'Cinematic', label: 'Cinematic' },
                {
                    value: 'Disney Charactor',
                    label: 'Disney Charactor',
                },
                {
                    value: 'Digital Art',
                    label: 'Digital Art',
                },
                {
                    value: 'Fantasy art',
                    label: 'Fantasy art',
                },
                { value: 'Neonpunk', label: 'Neonpunk' },
                { value: 'Enhance', label: 'Enhance' },
                {
                    value: 'Comic book',
                    label: 'Comic book',
                },
                { value: 'Lowpoly', label: 'Lowpoly' },
                { value: 'Line art', label: 'Line art' },
            ]}/>
                          </div>
                        </div>)}

                    {action === 'Edit Image with AI' &&
            totalUploadedImages > 0 && (<div className='block sm:col-span-6'>
                          <label htmlFor='editModel' className='block text-sm font-medium text-gray-700 dark:text-slate-400'>
                            {t('Model')}
                          </label>
                          <div className='mt-3'>
                            <EditModelSelector value={editModel} onChange={setEditModel} isFreePlan={isFreePlan} options={editModels.filter((model) => {
                if (totalUploadedImages > 2) {
                    return !model.value.includes('Flux Kontext');
                }
                return true;
            })}/>
                          </div>
                        </div>)}

                    <div className={clsxm(totalUploadedImages > 0 || hasActionParam
            ? 'hidden'
            : 'block', 'sm:col-span-6')}>
                      <label htmlFor='style' className='block text-sm font-medium text-gray-700 dark:text-slate-400'>
                        {t('Model')}
                      </label>
                      <div className='mt-2'>
                        <ModelSelectorButton value={selectedModels.length === 1
            ? selectedModels[0]
            : selectedModels} onChange={(e) => {
            const newModels = Array.isArray(e) ? e : [e];
            setSelectedModels(newModels as ImageModelType[]);
            // Only update training model for single selection
            if (newModels.length === 1) {
                const selectedModel = trainingModels.find((model) => model.name === newModels[0]);
                setSelectedTrainingModel(selectedModel || null);
            }
            else {
                setSelectedTrainingModel(null);
            }
        }} trainingModels={trainingModels} defaultTrainingModel={trainingModel}/>
                      </div>
                    </div>

                    {isPhotoStudioModelSelected && (<div className='sm:col-span-6'>
                        <label htmlFor='photo-studio-model' className='flex items-center text-sm font-medium text-gray-700 dark:text-slate-400'>
                          {t('Rendering model')}
                          <SimpleTooltip title={t('Choose how your photos are rendered. Hyperrealism gives the most lifelike results; Legacy is the previous Photo Studio engine.')}>
                            <QuestionMarkCircleIcon className='ml-1 h-4 w-4 text-gray-500'/>
                          </SimpleTooltip>
                        </label>
                        <div className='mt-2'>
                          <Select label='photo-studio-model' value={photoStudioModel} onChange={(value) => setPhotoStudioModel(value)} options={PHOTO_STUDIO_ENGINES.map((engine) => ({
                value: engine.value,
                label: engine.label,
            }))}/>
                        </div>
                      </div>)}

                    {/* {!selectedTrainingModel && (
          <div className='sm:col-span-6'>
            <SelfieModelBanner />
          </div>
        )} */}

                    {/* <div className='block sm:col-span-6'>
            <label
              htmlFor='style'
              className='block text-sm font-medium text-gray-700 dark:text-slate-400'
            >
              {t('Model')}
            </label>
            <div className='mt-1 flex rounded-md'>
              <Select
                onChange={(e) => setSelectedModels([e as ImageModelType])}
                label='dimensions'
                value={model}
                options={models}
              />
            </div>
          </div> */}
                    {model === 'Recraft v3' &&
            totalUploadedImages === 0 &&
            selectedModels.length === 1 && (<div className='sm:col-span-6'>
                          <label htmlFor='vector' className='flex text-sm font-medium text-gray-700 dark:text-slate-400'>
                            {t('Generate Vector Graphics')} (SVG)
                            <SimpleTooltip title={t('Generate images in SVG format for infinite scalability')}>
                              <QuestionMarkCircleIcon className='ml-1 h-4 w-4 text-gray-500'/>
                            </SimpleTooltip>
                            {isFreePlan && (<Link href='/settings/billing' className='ml-1 text-xs text-primary-500 hover:underline'>
                                {t('Upgrade')}
                              </Link>)}
                          </label>
                          <div className='mt-1 flex rounded-md'>
                            <Toggle initialValue={Boolean(useVector)} onChange={(option) => {
                if (isFreePlan) {
                    return false;
                }
                setUseVector(option);
                if (option) {
                    setRecraftStyle('vector_illustration');
                }
                else {
                    setRecraftStyle('any');
                }
            }} disabled={isFreePlan}/>
                          </div>
                        </div>)}
                    {model === 'Recraft v3' &&
            selectedModels.length === 1 &&
            totalUploadedImages === 0 && (<div className='sm:col-span-6'>
                          <label htmlFor='style' className='block text-sm font-medium text-gray-700 dark:text-slate-400'>
                            {t('Style (optional)')}
                          </label>
                          <div className='mt-1 flex rounded-md shadow-sm'>
                            <Select onChange={(value) => setRecraftStyle(value)} value={recraftStyle} label='style' options={useVector
                ? RECRAFT_VECTOR_STYLES.map((style) => ({
                    value: style,
                    label: style
                        .split('_')
                        .map((w) => w.charAt(0).toUpperCase() +
                        w.slice(1))
                        .join(' '),
                }))
                : RECRAFT_STYLES.map((style) => ({
                    value: style,
                    label: style
                        .split('/')
                        .map((word) => word
                        .split('_')
                        .map((w) => w.charAt(0).toUpperCase() +
                        w.slice(1))
                        .join(' '))
                        .join(' / '),
                }))}/>
                          </div>
                          <p className='mt-1 text-xs text-gray-500 dark:text-slate-400'>
                            {t('Select a style to apply to your generated image')}
                          </p>
                        </div>)}

                    {model === 'FLUX 1.1 Pro Ultra' && (<div className='sm:col-span-6'>
                        <label htmlFor='raw-mode' className='flex items-center text-sm font-medium text-gray-700 dark:text-slate-400'>
                          {t('Raw Mode')}
                          <SimpleTooltip title={t('For creators seeking authenticity, raw mode captures the genuine feel of candid photography, his feature generates images with a less synthetic, more natural aesthetic, increasing diversity in human subjects and enhancing the realism of nature photography')}>
                            <QuestionMarkCircleIcon className='ml-1 h-4 w-4 text-gray-500'/>
                          </SimpleTooltip>
                        </label>
                        <div className='mt-1 flex rounded-md'>
                          <Toggle initialValue={useRawMode} onChange={(option) => setUseRawMode(option)}/>
                        </div>
                      </div>)}
                    {model === 'FLUX 1.1 Pro' && (<div className='sm:col-span-6'>
                        <label htmlFor='promptUpsampling' className='flex items-center text-sm font-medium text-gray-700 dark:text-slate-400'>
                          <span className='mr-2'>Prompt Upsampling</span>
                          <SimpleTooltip title={t('More creative generation')}>
                            <QuestionMarkCircleIcon className='h-4 w-4 text-gray-500'/>
                          </SimpleTooltip>
                        </label>
                        <div className='mt-1 flex rounded-md'>
                          <Toggle initialValue={promptUpsampling} onChange={(option) => setPromptUpsampling(option)}/>
                        </div>
                      </div>)}
                    {(totalUploadedImages > 0 || hasActionParam) && (<div className='sm:col-span-6'>
                        <label htmlFor='action' className='block text-sm font-medium text-gray-700 dark:text-slate-400'>
                          {t('Action')}
                        </label>
                        <div className='mt-2 flex rounded-md'>
                          <ActionSelection selectedAction={action} onChange={(e) => {
                setAction(e);
                if (e === ('Colorize' as ImageModelType)) {
                    setPrompt('Colorize this image');
                }
                if (e === ('Caricature' as ImageModelType)) {
                    setPrompt('Caricature of this image');
                }
                if (e === ('Halloween' as ImageModelType)) {
                    setPrompt('Create 15 Halloween-themed photos from uploaded images');
                }
                if (e === ('Ghiblify' as ImageModelType)) {
                    setPrompt('Ghiblify this image');
                }
                if (e === ('Muppets' as ImageModelType)) {
                    setPrompt('Turn this image into a muppet');
                }
                if (e ===
                    ('Visualize What Happens Next' as ImageModelType)) {
                    setPrompt('Visualize what happens next from this image');
                }
                if (e === ('Stylization' as ImageModelType)) {
                    setPrompt('transform to different styles');
                }
                if (e === ('Realistic Photos' as ImageModelType)) {
                    setPrompt('a man wearing a doctoral cap');
                }
                if (e ===
                    ('Professional Headshot' as ImageModelType)) {
                    setPrompt('Professional headshot of this image');
                }
                if (e === ('Remove Background' as ImageModelType)) {
                    setPrompt('Remove background from this image');
                }
            }}/>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>

              <div className='sticky bottom-0 right-0 z-10 flex justify-between items-center w-full flex-col space-y-4 border-b border-t border-gray-200 bg-gray-100 px-4 py-4 dark:border-white/10 dark:bg-slate-800 sm:px-6'>
                <div className='flex w-full flex-col gap-3'>
                  {isPanoramaStyle && (<div className='flex items-center gap-2 rounded-md bg-indigo-50 px-3 py-2 text-xs text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'>
                      <GlobeAltIcon className='h-4 w-4 flex-shrink-0'/>
                      <span>
                        {t('360° Panorama uses GPT Image 2 at 2:1 equirectangular — model and ratio are set automatically.')}
                      </span>
                    </div>)}
                  <div className='flex items-center gap-3'>
                    {action !== 'Visualize What Happens Next' && (<>
                        <div className=''>
                          <AspectRatioSelector value={dimensions} onChange={setDimensions} options={dimensionsOptions} disabledOptions={subscriptionName === 'Free'
                ? [
                    '1536x1536',
                    '2048x1536',
                    '1536x2048',
                    '2048x2048',
                    '1792x1024',
                    '1024x1792',
                    '16:9',
                    '21:9',
                    '2:3',
                    '3:2',
                    '4:5',
                    '5:4',
                    '9:16',
                    '9:21',
                    '2048x1536',
                    '1536x2048',
                ]
                : []}/>
                        </div>
                        {availableResolutions.length > 0 && (<ResolutionSelector value={resolution &&
                    availableResolutions.includes(resolution)
                    ? resolution
                    : availableResolutions[0]} onChange={(value) => setResolution(value)} options={availableResolutions} lockedOptions={isFreePlan ? ['4K'] : []}/>)}
                        <div className='flex items-center'>
                          {/* <label
          htmlFor='outputs'
          className='mr-2 flex-nowrap text-sm font-medium text-gray-400'
        >
          {t('images')}
        </label> */}
                          <NumberInput value={numberOfOutputs} onChange={setNumberOfOutputs} min={(() => {
                switch (model) {
                    case 'Midjourney V6':
                    case 'Midjourney V7':
                        return 4;
                    default:
                        return 1;
                }
            })()} max={(() => {
                switch (model) {
                    case 'Midjourney V6':
                    case 'Midjourney V7':
                        return 4;
                    case 'Flux Kontext Pro':
                    case 'Flux Kontext Max':
                    case 'Flux.1 Krea':
                    case 'FLUX 1.1 Pro':
                    case 'FLUX.1':
                        return 8;
                    default:
                        return 4;
                }
            })()}/>
                        </div>
                      </>)}
                    <div className='ml-auto'>
                      <button type='button' onClick={clearInputs} className='flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'>
                        <XIcon className='h-3.5 w-3.5'/>
                        <span>{t('Clear inputs')}</span>
                      </button>
                    </div>
                  </div>
                  {localTotalImages >= imagesInPlan || shouldUpgrade ? (<div className='flex w-full flex-wrap items-center justify-end gap-2'>
                      {/* Paid users who run out want the credit pack, not a
                plan page with no credit packs on it — make Buy the
                primary action and open the pack modal right away.
                (Customer spent 2h+ bouncing off /settings/billing.) */}
                      <CreditsDisplay totalUsed={localTotalImages} totalAvailable={imagesInPlan} bonusCredits={bonusImages} subscriptionName={subscriptionName} isFreePlan={isFreePlan} type='images' showBuyButton={true} buttonVariant={isFreePlan ? 'outline' : 'dark'} autoOpenWhenOut={!shouldUpgrade}/>
                      <ButtonLink href='/settings/billing' variant={isFreePlan ? 'dark' : 'outline'} className='ml-2'>
                        {t('Upgrade plan')}
                      </ButtonLink>
                    </div>) : (<div className='flex w-full flex-col gap-2'>
                      {/* Check if any uploaded image is flagged */}
                      {hasFlaggedUpload && (<div className='mr-2 flex items-center text-red-600 text-sm'>
                          <span>
                            ⚠️ {t('Content policy violation detected')}
                          </span>
                        </div>)}
                      {/* Show warning if blob URLs exist */}
                      {hasUploadingImage && (<div className='mr-2 flex items-center text-yellow-600 text-sm'>
                          <span>⏳ {t('Uploading images...')}</span>
                        </div>)}
                      <Button type='submit' isLoading={pendingImageCount > 0 ||
                mutation.isLoading ||
                isFileLoading} disabled={mutation.isLoading ||
                pendingImageCount > 0 ||
                isFileLoading ||
                hasUploadingImage ||
                hasFlaggedUpload} variant={hasFlaggedUpload || hasUploadingImage
                ? 'light'
                : 'dark'} className='xl:px-6 xl:py-3 xl:text-base w-full text-center justify-center'>
                        <span>
                          {hasFlaggedUpload
                ? t('Content Blocked')
                : hasUploadingImage
                    ? t('Uploading...')
                    : t('Generate')}{' '}
                          (
                          {/* {selectedModels.includes('Nano Banana Pro') && (
              <>
                <span className='line-through text-gray-400 mr-1'>
                  {4 * numberOfOutputs}
                </span>
                <SimpleTooltip
                  title={t(
                    'Promotional pricing for Nano Banana Pro until Dec 31',
                  )}
                >
                  <QuestionMarkCircleIcon className='inline h-3.5 w-3.5 text-gray-400 mr-1 cursor-help' />
                </SimpleTooltip>
              </>
            )} */}
                          {`${credits * numberOfOutputs}`}{' '}
                          {credits * numberOfOutputs > 1
                ? t('credits')
                : t('credit')}
                          )
                        </span>
                      </Button>
                    </div>)}
                </div>
                {isFreePlan && (<div className='flex items-center self-end'>
                    <label htmlFor='is-public' className='flex items-center text-sm font-medium text-gray-700 dark:text-slate-400'>
                      {t('Is Public')}
                      <SimpleTooltip title={t('Images in the Free plan are public. Upgrade to a paid plan for private images and full commercial rights to your generated content.')}>
                        <QuestionMarkCircleIcon className='ml-1 h-4 w-4 text-gray-500'/>
                      </SimpleTooltip>
                    </label>
                    <div className='ml-2'>
                      <Toggle initialValue={isPublic} isSmall={true} cannotBeToggled={true} onChange={(option) => {
                if (!option) {
                    toast.info(<div>
                                <div className='mb-5'>
                                  {t('Upgrade to a Pro plan for private images and full commercial rights to your generated content.')}
                                </div>
                                <ButtonLink variant='dark' href='/settings/billing'>
                                  {t('Upgrade plan')}
                                </ButtonLink>
                              </div>);
                    return;
                }
                setIsPublic(true);
            }}/>
                    </div>
                  </div>)}
              </div>
            </form>
          </div>
          <div className='hidden lg:block lg:w-[33.3333vw] lg:max-w-[480px]'></div>
          <div className='z-15 pointer-events-auto w-full px-2 py-4 pb-16 opacity-100 duration-150 dark:bg-slate-800 lg:flex-1 lg:overflow-y-auto lg:px-6 lg:py-4 lg:pb-16'>
            {generatedImages.length === 0 &&
            pendingImageCount === 0 &&
            isLoading &&
            user && (<div className='flex h-full items-center justify-center'>
                  <Spinner className='animate-spin-fast h-8 w-8 text-primary-500'/>
                </div>)}
            {(!isLoading ||
            !user ||
            generatedImages.length > 0 ||
            pendingImageCount > 0) && (<section>
                {/* Tab Switches and View Mode Selector */}
                <div className='mb-4 flex items-center justify-between'>
                  <div className='flex flex-wrap gap-1.5'>
                    <button onClick={() => setActiveTab('images')} className={clsxm('flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200', activeTab === 'images'
                ? 'bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-md dark:from-gray-100 dark:to-gray-200 dark:text-gray-900'
                : 'bg-white dark:bg-gray-800 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 border border-gray-200 dark:border-gray-700 hover:shadow-sm')}>
                      <CollectionIcon className='h-3.5 w-3.5'/>
                      <span>{t('Images')}</span>
                    </button>
                    <button onClick={() => setActiveTab('templates')} className={clsxm('flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200', activeTab === 'templates'
                ? 'bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-md dark:from-gray-100 dark:to-gray-200 dark:text-gray-900'
                : 'bg-white dark:bg-gray-800 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 border border-gray-200 dark:border-gray-700 hover:shadow-sm')}>
                      <SparklesIcon className='h-3.5 w-3.5'/>
                      <span>{t('Templates')}</span>
                    </button>
                    <button onClick={() => setActiveTab('tools')} className={clsxm('flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200', activeTab === 'tools'
                ? 'bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-md dark:from-gray-100 dark:to-gray-200 dark:text-gray-900'
                : 'bg-white dark:bg-gray-800 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 border border-gray-200 dark:border-gray-700 hover:shadow-sm')}>
                      <CubeTransparentIcon className='h-3.5 w-3.5'/>
                      <span>{t('AI Image Tools')}</span>
                    </button>
                    <button onClick={() => setActiveTab('examples')} className={clsxm('flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200', activeTab === 'examples'
                ? 'bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-md dark:from-gray-100 dark:to-gray-200 dark:text-gray-900'
                : 'bg-white dark:bg-gray-800 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 border border-gray-200 dark:border-gray-700 hover:shadow-sm')}>
                      <PhotographIcon className='h-3.5 w-3.5'/>
                      <span>{t('Examples')}</span>
                    </button>
                  </div>
                  {activeTab === 'images' && (<ViewModeSelector viewMode={viewMode} onViewModeChange={setViewMode}/>)}
                </div>

                {activeTab === 'images' &&
                (generatedImages.length > 0 || pendingImageCount > 0) && (<>
                      <div className='' ref={generatedCaptions}>
                        <ul role='list' className={clsxm(viewMode?.layout === 'row'
                    ? 'flex flex-col gap-y-4'
                    : viewMode?.size === 'small'
                        ? 'grid grid-cols-3 gap-2 sm:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6'
                        : viewMode?.size === 'large'
                            ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:gap-6'
                            : 'grid grid-cols-2 gap-x-2 gap-y-2 sm:gap-x-4 sm:gap-y-2 xl:grid-cols-3 xl:gap-x-2 2xl:grid-cols-4')}>
                          {Array.from({ length: pendingImageCount }, (_, index) => (<li key={`pending-${index}`}>
                                <ImageGenerationPreview statusLabel={t('Generating...')} generationStartedAt={pendingGeneration?.startedAt} naturalAspect={viewMode?.layout === 'row' &&
                        viewMode?.fill !== 'square'}/>
                              </li>))}
                          {generatedImages.map((image) => image.is_video ? (<VideoOutput key={image.id} video={image} viewMode={viewMode}/>) : (<ImageOutput key={image.id} image={image} viewMode={viewMode} animateReveal={newImageStartTimes.current.has(String(image.id))} generationStartedAt={newImageStartTimes.current.get(String(image.id))} isNotFirstSessionImage={(generationCount ?? 0) > 1}/>))}
                        </ul>
                      </div>
                      <div className='mt-8 flex justify-center'>
                        <ButtonLink variant='outline' href='/history/images'>
                          {t('History')} →
                        </ButtonLink>
                      </div>
                    </>)}

                {activeTab === 'images' &&
                generatedImages.length === 0 &&
                pendingImageCount === 0 && (<div className='flex flex-col items-center justify-center py-16 px-4'>
                      <div className='relative mb-6'>
                        <div className='absolute inset-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-pink-500/20 blur-2xl'/>
                        <div className='relative rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800'>
                          <PhotographIcon className='h-10 w-10 text-gray-400 dark:text-gray-500'/>
                        </div>
                      </div>
                      <h2 className='mb-2 text-xl font-semibold text-gray-900 dark:text-white'>
                        {t('No images yet')}
                      </h2>
                      <p className='mb-8 max-w-sm text-center text-sm text-gray-500 dark:text-gray-400'>
                        {t('Describe what you want to see and let AI bring it to life.')}
                      </p>
                      <div className='flex flex-wrap items-center justify-center gap-2 max-w-lg'>
                        {EMPTY_STATE_SUGGESTIONS.map((suggestion) => (<button key={suggestion} type='button' onClick={() => {
                        setPrompt(suggestion);
                        document.getElementById('prompt')?.focus();
                    }} className='rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-xs text-gray-600 shadow-sm transition-all hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-primary-500 dark:hover:bg-primary-900/30 dark:hover:text-primary-400'>
                            {suggestion}
                          </button>))}
                      </div>
                    </div>)}

                {activeTab === 'tools' && (<div className='ai-image-tools'>
                    <ImageToolsGrid />
                  </div>)}

                {activeTab === 'templates' && (<TemplatesGallery templates={imageTemplates} fallbackNamespace='ai-images' currentPath='/ai-images'/>)}

                {activeTab === 'examples' && (<ExamplesTab onRecreateButtonClick={setPrompt}/>)}
              </section>)}
          </div>
        </main>
      </main>
      <SceneModal isOpen={sceneModalTab !== null} onClose={() => setSceneModalTab(null)} initialTab={sceneModalTab ?? 'style'} values={{
            style,
            camera: cinematicState?.camera ?? 'Standard',
            lens: cinematicState?.lens ?? 'Standard',
            focalLength: cinematicState?.focalLength ?? 'Standard',
            aperture: cinematicState?.aperture ?? 'Standard',
            lighting: cinematicState?.lighting ?? 'None',
            shotType: cinematicState?.shotType ?? 'None',
        }} selectedCharacters={newBeforeImages} selectedObjects={newObjectImages} selectedImages={newUploadImages} onApply={(next) => {
            setStyle(next.style);
            setCinematicState({
                ...(cinematicState ?? DEFAULT_CINEMATIC_STATE),
                camera: next.camera,
                lens: next.lens,
                focalLength: next.focalLength,
                aperture: next.aperture,
                lighting: next.lighting,
                shotType: next.shotType,
            });
        }} onLibrarySelectionApply={({ characters, objects, images }) => {
            setNewBeforeImages(characters);
            setNewObjectImages(objects);
            setNewUploadImages(images);
        }}/>
      {showPhotoPacks && (<PhotoPacksSelector isOpen={showPhotoPacks} onClose={closePhotoPacks} trainingModels={trainingModels} selectedTrainingModel={selectedTrainingModel} initialPackSlugs={photoPackDraft} initialModelId={photoPackModelId} onSignIn={(returnPath) => {
                const query = new URL(returnPath, 'https://easy-peasy.ai')
                    .searchParams;
                setPhotoPackDraft(parsePhotoPackSlugs(query.get('packs') || undefined));
                setPhotoPackModelId(Number(query.get('packModel')) || null);
                setShowPhotoPacks(false);
                setAuthRedirectTo(`${router.locale && router.locale !== 'en' ? `/${router.locale}` : ''}${returnPath}`);
                setShowAuth(true);
            }} onPacksGenerated={() => {
                setActiveTab('images');
                client.invalidateQueries(['image-generator-images']);
            }}/>)}
      <AuthModal showModal={showAuth} onClose={() => setShowAuth(false)} afterClose={() => {
            if (authRedirectTo.includes('photoPacks=1'))
                setShowPhotoPacks(true);
            setAuthRedirectTo('/ai-images');
        }} redirectTo={authRedirectTo}/>
    </NarrowDashboardLayout>);
};
export default ImagesPage;
async function getServerSideProps(context: GetServerSidePropsContext) {
    const supabase = createClient(context);
    // get training model by trained_model_id
    const { modelId: trained_model_id } = context.query;
    const { data: { user }, } = await supabase.auth.getUser();
    if (!user) {
        return {
            props: {
                imagesInPlan: 0,
                trainingModel: null,
                trainingModels: [],
                country: (context.req.headers['cf-ipcountry'] as string) || null,
            },
        };
    }
    const [userDetails, trainingModel, trainingModels] = await Promise.all([
        getProfile(user.id),
        typeof trained_model_id === 'string'
            ? getTrainingModelByTrainedModelIdAndUserId(trained_model_id, user.id)
            : Promise.resolve(null),
        getTrainingModelsByUserIdAndStatus(user.id, 'completed'),
    ]);
    const userSubscription = await getSubscriptionForEdge(user.id, userDetails?.team_id);
    // const totalImages = await getImageCreditsUsedFromTimestamp(
    //   user.id,
    //   userSubscription?.current_period_start as string
    // );
    const subscriptionName = ((userSubscription &&
        userSubscription?.prices?.products?.name) ||
        'Free') as SubscriptionName;
    const imagesInPlan = getImagesInPlan(subscriptionName, userSubscription?.status) +
        Number(userDetails?.bonus_images || 0);
    // const images = await getImages(user.id, userDetails?.team_id, false, 32);
    return {
        props: {
            //totalImages: Number(totalImages),
            imagesInPlan,
            trainingModel: trainingModel
                ? JSON.parse(JSON.stringify(trainingModel))
                : null,
            trainingModels: trainingModels
                ? JSON.parse(JSON.stringify(trainingModels))
                : [],
            country: (context.req.headers['cf-ipcountry'] as string) || null,
            // images,
        },
    };
}

    async function __Next_Translate__getServerSideProps__1a0ced21aa8__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-images/index',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a0ced21aa8__ as getServerSideProps }
  